import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Upload, Trash2, Check, X, Plus, RefreshCw, AlertCircle, ArrowUpDown, FileDown, Wand2, FileSpreadsheet, Edit2, FileText, Lock, Unlock, Search } from 'lucide-react';
import api from '../../api/client';
import ControlloBanca from './ControlloBanca';
import Contanti from './Contanti';

const fmt = (n) => `€${Math.abs(n||0).toFixed(2)}`;
const MESI = ['','Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

function CategoriaCombobox({ tx, categorie, onAssign, onCreaEAssegna }) {
  const [val, setVal]   = useState(tx.categoria?.nome || '');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVal(tx.categoria?.nome || ''); }, [tx.categoria]);

  const filtered   = val.trim() ? categorie.filter(c => c.nome.toLowerCase().includes(val.toLowerCase())) : categorie;
  const exactMatch = categorie.some(c => c.nome.toLowerCase() === val.trim().toLowerCase());
  const showCreate = val.trim().length > 0 && !exactMatch;

  const handleSelect = async (cat) => {
    setVal(cat.nome);
    setOpen(false);
    onAssign(tx.id, cat.id);
  };

  const handleCreate = async () => {
    if (!val.trim() || busy) return;
    setBusy(true);
    try { await onCreaEAssegna(tx.id, val.trim(), tx.importo); setOpen(false); }
    finally { setBusy(false); }
  };

  const borderColor = tx.categoria_id ? '1px solid #22C55E40' : '1px solid #F59E0B40';
  const bgColor     = tx.categoria_id ? '#22C55E08' : '#F59E0B08';

  return (
    <div style={{ position:'relative' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'3px' }}>
        <input
          type="text" value={val}
          onChange={e => { setVal(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          placeholder="— categoria —"
          style={{ background: bgColor, border: borderColor, borderRadius:'8px', padding:'5px 8px', color:'var(--text,#fff)', fontSize: 'var(--font-size-xs)', outline:'none', width:'100%', boxSizing:'border-box', fontFamily:"inherit" }}
        />
        {busy && <RefreshCw size={11} color="#F59E0B" style={{ flexShrink:0 }}/>}
        {!busy && tx.categoria_id && (
          <button onMouseDown={e => { e.preventDefault(); setVal(''); onAssign(tx.id, null); }}
            style={{ background:'none', border:'none', color:'#555', cursor:'pointer', padding:'2px', flexShrink:0 }}>
            <X size={10}/>
          </button>
        )}
      </div>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200,
          background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)',
          borderRadius:'8px', boxShadow:'0 4px 20px rgba(0,0,0,0.6)',
          maxHeight:'180px', overflowY:'auto', marginTop:'2px' }}>
          {filtered.map(c => (
            <div key={c.id} onMouseDown={() => handleSelect(c)}
              style={{ padding:'7px 10px', fontSize: 'var(--font-size-xs)', cursor:'pointer', color:'var(--text,#fff)',
                borderBottom:'1px solid var(--border,#1E1E1E)' }}
              onMouseEnter={e => e.currentTarget.style.background='var(--border2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              {c.nome}
            </div>
          ))}
          {filtered.length === 0 && !showCreate && (
            <div style={{ padding:'7px 10px', fontSize: 'var(--font-size-xs)', color:'#555' }}>Nessuna categoria trovata</div>
          )}
          {showCreate && (
            <div onMouseDown={handleCreate}
              style={{ padding:'7px 10px', fontSize: 'var(--font-size-xs)', cursor:'pointer', color:'#F4600C',
                fontWeight:'700', borderTop: filtered.length ? '1px solid var(--border2,#2A2A2A)' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background='var(--border2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              + Crea "{val.trim()}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MovimentiBancari() {
  const [tab, setTab]               = useState('movimenti');
  const [categorie, setCategorie]   = useState([]);
  const [casse, setCasse]           = useState([]);
  const [transazioni, setTransazioni] = useState([]);
  const [loading, setLoading]       = useState(false);

  // Filtri
  const [filtroAnno, setFiltroAnno]       = useState(new Date().getFullYear());
  const [filtroMese, setFiltroMese]       = useState('');
  const [filtroTrimestre, setFiltroTrimestre] = useState('');
  const [filtroTipo, setFiltroTipo]       = useState('');
  const [filtroCat, setFiltroCat]         = useState('');
  const [filtroCassa, setFiltroCassa]     = useState('');
  const [cerca, setCerca]                 = useState(''); // ricerca libera (16/10, titolare): sui movimenti già filtrati
  const [pagina, setPagina]               = useState(0);  // 50 righe per pagina: la tabella si disegna subito
  const [orderBy, setOrderBy]             = useState('data');
  const [orderDir, setOrderDir]           = useState('desc');
  const [savingId, setSavingId]           = useState({});
  const [saldoFineTrimestre, setSaldoFineTrimestre] = useState(null);

  // File importati
  const [fileImportati, setFileImportati] = useState([]);

  // Import
  const [showImport, setShowImport]   = useState(false);
  const [importAnno, setImportAnno]   = useState(new Date().getFullYear());
  const [importCassa, setImportCassa] = useState('');
  const [importing, setImporting]     = useState(false);
  const [preview, setPreview]         = useState(null);
  const fileRef = useRef();

  // Auto-categorizzazione
  const [showAutocat, setShowAutocat]         = useState(false);
  const [autocatLoading, setAutocatLoading]   = useState(false);
  const [suggerimenti, setSuggerimenti]       = useState([]);
  const [scelteSugg, setScelteSugg]           = useState({});
  const [applyingAutocat, setApplyingAutocat] = useState(false);
  const [autocatMese, setAutocatMese]         = useState('');
  // Wizard
  const [showWizard, setShowWizard]           = useState(false);
  const [wizardLoading, setWizardLoading]     = useState(false);
  const [wizardGruppi, setWizardGruppi]       = useState([]);
  const [wizardScelte, setWizardScelte]       = useState({});   // chiave → { categoria_id, categoria_nome }
  const [wizardMese, setWizardMese]           = useState('');
  const [applyingWizard, setApplyingWizard]   = useState(false);
  const [wizardAperti, setWizardAperti]       = useState(new Set()); // chiavi dei gruppi espansi
  const [wizardEsclusi, setWizardEsclusi]     = useState(new Set()); // id transazioni tolte dal proprio gruppo

  // Categorie / Casse
  const [showAddCat, setShowAddCat]     = useState(false);
  const [newCat, setNewCat]             = useState({ nome:'', tipo:'ENTRATA', centro_id:'', colore:'#3B82F6', parole_chiave:'' });
  const [centri, setCentri]             = useState([]); // centri di costo/ricavo: la categoria appartiene a un centro
  const [editCat, setEditCat]           = useState(null); // { id, nome, centro_id, parole_chiave } in modifica
  const [satDettaglio, setSatDettaglio] = useState({}); // id accredito Satispay → { loading, data, error }
  const [showAddCassa, setShowAddCassa] = useState(false);
  const [newCassa, setNewCassa]         = useState({ nome:'', tipo:'BANCA', iban:'', colore:'#3B82F6' });
  const [editSaldo, setEditSaldo]       = useState({}); // cassa_id → { saldo_iniziale, data_saldo_iniziale }
  const [verificaBanca, setVerificaBanca] = useState(0); // fa ripartire il controllo con la banca
  const [saldoCalcolato, setSaldoCalcolato] = useState(null); // { saldo_iniziale, data, tot_movimenti, saldo_calcolato }

  // Satispay
  const [satispayPayments, setSatispayPayments] = useState([]);
  const [loadingSatispay, setLoadingSatispay]   = useState(false);
  const [satispayError, setSatispayError]       = useState(null);

  // Note inline editing
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteTemp, setNoteTemp]           = useState('');

  // Estratti conto
  const [estrattiData, setEstrattiData]   = useState(null); // { estratti, totali }
  const [estrattiAnno, setEstrattiAnno]   = useState(new Date().getFullYear());
  const [uploadingQ, setUploadingQ]       = useState(null); // trimestre in upload
  const [estrattoMsg, setEstrattomsg]     = useState('');
  const estrattoRef                       = useRef();

  // KPI server-side (totali accurati indipendentemente dalle righe caricate)
  const [kpi, setKpi] = useState({ entrate: 0, uscite: 0, totale: 0, count: 0 });

  // Inserimento manuale
  const [showManuale, setShowManuale]     = useState(false);
  const [nuovaTx, setNuovaTx]             = useState({ data:'', operazione:'', dettagli:'', importo:'', contabilizzazione:'', conto:'', categoria_id:'', cassa_id:'', note:'', movimentato_da:'' });
  const [salvandoManuale, setSalvandoManuale] = useState(false);

  const loadSatispay = useCallback(async () => {
    setLoadingSatispay(true);
    setSatispayError(null);
    try {
      const r = await api.get('/satispay/payments?limit=100');
      setSatispayPayments(r.data.payments || []);
    } catch(e) { setSatispayError('Errore caricamento pagamenti Satispay'); }
    finally { setLoadingSatispay(false); }
  }, []);

  const loadBase = useCallback(async () => {
    try {
      const [cat, cas] = await Promise.all([api.get('/banca/categorie'), api.get('/banca/casse')]);
      let c = cat.data, cs = cas.data;
      if (c.length === 0) { const r = await api.post('/banca/categorie/seed', {}); c = r.data; }
      if (cs.length === 0) { const r = await api.post('/banca/casse/seed', {}); cs = r.data; }
      setCategorie(c);
      setCasse(cs);
      // i centri hanno un permesso a parte: senza, le categorie restano visibili ma senza centro selezionabile
      api.get('/centri').then(r => setCentri(r.data)).catch(() => setCentri([]));
    } catch(e) { console.error(e); }
  }, []);

  const fineTrimestre = (anno, q) => ({ 1:`${anno}-03-31`, 2:`${anno}-06-30`, 3:`${anno}-09-30`, 4:`${anno}-12-31` }[q]);

  useEffect(() => { setPagina(0); }, [cerca, filtroAnno, filtroMese, filtroTrimestre, filtroTipo, filtroCat, filtroCassa, orderBy, orderDir]);

  const loadTransazioni = useCallback(async () => {
    setLoading(true);
    setSaldoFineTrimestre(null);
    try {
      const params = new URLSearchParams({ anno: filtroAnno, orderBy, orderDir });
      if (filtroMese)      params.set('mese', filtroMese);
      if (filtroTrimestre && !filtroMese) params.set('trimestre', filtroTrimestre);
      if (filtroTipo)      params.set('tipo', filtroTipo);
      if (filtroCat === '__NONE__') params.set('senza_categoria', '1');
      else if (filtroCat)  params.set('categoria_id', filtroCat);
      if (filtroCassa)     params.set('cassa_id', filtroCassa);
      else                 params.set('solo_banca', '1'); // i contanti stanno nella scheda CONTANTI

      const kpiParams = new URLSearchParams({ anno: filtroAnno });
      if (filtroMese)      kpiParams.set('mese', filtroMese);
      if (filtroTrimestre && !filtroMese) kpiParams.set('trimestre', filtroTrimestre);
      if (filtroCat && filtroCat !== '__NONE__') kpiParams.set('categoria_id', filtroCat);
      if (filtroCassa)     kpiParams.set('cassa_id', filtroCassa);
      else                 kpiParams.set('solo_banca', '1');

      const [txRes, kpiRes] = await Promise.all([
        api.get('/banca/transazioni?' + params.toString()),
        api.get('/banca/transazioni/kpi?' + kpiParams.toString()),
      ]);
      setTransazioni(txRes.data);
      setKpi(kpiRes.data);

      // Saldo al fine trimestre se è selezionato un trimestre e c'è una cassa
      if (filtroTrimestre && filtroCassa) {
        const al = fineTrimestre(filtroAnno, +filtroTrimestre);
        try {
          const sr = await api.get(`/banca/casse/${filtroCassa}/saldo?al=${al}`);
          setSaldoFineTrimestre(sr.data);
        } catch { setSaldoFineTrimestre(null); }
      } else if (filtroTrimestre && !filtroCassa) {
        // cerca prima cassa bancaria con saldo iniziale
        const primaCassa = casse.find(c => c.tipo === 'BANCA' && c.saldo_iniziale != null);
        if (primaCassa) {
          const al = fineTrimestre(filtroAnno, +filtroTrimestre);
          try {
            const sr = await api.get(`/banca/casse/${primaCassa.id}/saldo?al=${al}`);
            setSaldoFineTrimestre(sr.data);
          } catch { setSaldoFineTrimestre(null); }
        }
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [filtroAnno, filtroMese, filtroTrimestre, filtroTipo, filtroCat, filtroCassa, orderBy, orderDir, casse]);

  const loadFileImportati = useCallback(async () => {
    try {
      const r = await api.get('/banca/import/files');
      // Estrae anno dai primi 4 caratteri del nome file (es. "2026_banca_..." → 2026)
      const lista = r.data.map(f => ({
        fileName: f.fonte_file,
        anno: parseInt(f.fonte_file?.slice(0, 4)) || new Date().getFullYear(),
        n_transazioni: f.n_transazioni,
        importo_netto: f.importo_netto,
      })).sort((a, b) => b.anno - a.anno || b.fileName.localeCompare(a.fileName));
      setFileImportati(lista);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { if (tab === 'movimenti') loadTransazioni(); }, [tab, loadTransazioni]);
  // reset saldo trimestre quando si toglie il filtro
  useEffect(() => { if (!filtroTrimestre) setSaldoFineTrimestre(null); }, [filtroTrimestre]);
  useEffect(() => { if (tab === 'files') loadFileImportati(); }, [tab, loadFileImportati]);
  useEffect(() => { if (tab === 'satispay') loadSatispay(); }, [tab, loadSatispay]);
  useEffect(() => { if (tab === 'estratti') loadEstrattiAnno(); }, [tab, estrattiAnno]);

  // ── IMPORT (multi-file) ─────────────────────────────────
  const leggiFileBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => resolve(ev.target.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setImporting(true);
    try {
      const cassaObj = casse.find(c => c.nome === importCassa);
      let tutteNuove = [], tutteDuplicate = [];

      const filesSalvati = []; // { base64, savedFileName }
      for (const file of files) {
        const base64 = await leggiFileBase64(file);
        // Archivia il file su disco, ricevi il nome salvato
        const uploadRes = await api.post('/banca/file/upload', {
          fileData: base64, fileName: file.name,
          anno: importAnno, cassa: importCassa || 'banca',
        });
        filesSalvati.push({ base64, savedFileName: uploadRes.data.fileName });
      }

      let tutteSaltate = [];

      for (const { base64, savedFileName } of filesSalvati) {
        const parseRes   = await api.post('/banca/file/parse', { fileData: base64 });
        const { righe, saltate, totaleRigheXlsx } = parseRes.data;
        const righeConFonte = righe.map(r => ({ ...r, fonte_file: savedFileName }));
        const previewRes = await api.post('/banca/import/preview', { rows: righeConFonte, cassa_id: cassaObj?.id });
        tutteNuove     = tutteNuove.concat(previewRes.data.nuove);
        tutteDuplicate = tutteDuplicate.concat(previewRes.data.duplicate);
        if (saltate?.length) tutteSaltate = tutteSaltate.concat(saltate.map(s => ({ ...s, file: savedFileName })));
      }

      // Deduplicazione cross-file
      const visti = new Set();
      tutteNuove = tutteNuove.filter(r => { if (visti.has(r.hash)) return false; visti.add(r.hash); return true; });

      setPreview({ nuove: tutteNuove, duplicate: tutteDuplicate, saltate: tutteSaltate, cassaId: cassaObj?.id, nFiles: files.length });
    } catch(err) {
      alert('Errore analisi: ' + (err.response?.data?.error || err.message));
    } finally { setImporting(false); }
    e.target.value = '';
  };

  const confermaImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      // Raggruppa per fonte_file e invia un confirm per ciascuno
      const perFile = preview.nuove.reduce((g, r) => {
        const k = r.fonte_file || '__nessuno__';
        if (!g[k]) g[k] = [];
        g[k].push(r);
        return g;
      }, {});

      let totImportate = 0, totAggiornate = 0;
      for (const [fonteFile, rows] of Object.entries(perFile)) {
        const r = await api.post('/banca/import/confirm', {
          rows,
          cassa_id: preview.cassaId,
          fonte_file: fonteFile === '__nessuno__' ? null : fonteFile,
        });
        totImportate += r.data.importate || 0;
        totAggiornate += r.data.aggiornate || 0;
      }
      if (totAggiornate > 0) alert(`${totImportate} movimenti nuovi, ${totAggiornate} movimenti già caricati aggiornati (in attesa ora contabilizzati, o bonifici con il testo completato dalla banca).`);
      setVerificaBanca(v => v + 1);

      setPreview(null);
      setShowImport(false);
      loadTransazioni();
      loadFileImportati();
      if (totImportate > 0) apriAutocat(true);
    } catch(e) {
      alert('Errore import: ' + (e.response?.data?.error || e.message));
    } finally { setImporting(false); }
  };

  // ── AUTO-CATEGORIZZAZIONE ────────────────────────────────
  const apriAutocat = async (silenzioso = false) => {
    if (!silenzioso) setShowAutocat(true);
    setAutocatLoading(true);
    try {
      const r = await api.get('/banca/transazioni/suggerimenti');
      setSuggerimenti(r.data.suggerimenti);
      // Pre-popola le scelte con i suggerimenti automatici
      const scelte = {};
      r.data.suggerimenti.forEach(s => {
        if (s.categoria_id) scelte[s.id] = { categoria_id: s.categoria_id, categoria_nome: s.categoria_nome };
      });
      setScelteSugg(scelte);
      if (!silenzioso || r.data.suggerimenti.length > 0) setShowAutocat(true);
    } catch(e) { console.error(e); }
    finally { setAutocatLoading(false); }
  };

  const applicaAutocat = async () => {
    const applicazioni = Object.entries(scelteSugg)
      .filter(([, v]) => v?.categoria_id)
      .map(([id, v]) => ({ id: parseInt(id), categoria_id: v.categoria_id }));
    if (!applicazioni.length) return;
    setApplyingAutocat(true);
    try {
      await api.post('/banca/transazioni/applica-suggerimenti', { applicazioni });
      setShowAutocat(false);
      setSuggerimenti([]);
      setScelteSugg({});
      loadTransazioni();
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setApplyingAutocat(false); }
  };

  // ✓ sulla singola riga: applica solo quella, le altre restano in finestra
  const [applicandoUna, setApplicandoUna] = useState(null);
  const applicaUnaSugg = async (txId) => {
    const v = scelteSugg[txId];
    if (!v?.categoria_id) return;
    setApplicandoUna(txId);
    try {
      await api.post('/banca/transazioni/applica-suggerimenti', { applicazioni: [{ id: txId, categoria_id: v.categoria_id }] });
      setSuggerimenti(p => p.filter(x => x.id !== txId));
      setScelteSugg(p => { const n = { ...p }; delete n[txId]; return n; });
      loadTransazioni();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setApplicandoUna(null); }
  };

  const aggiornaSceltaSugg = (txId, categoriaId, categoriaNome) => {
    setScelteSugg(p => ({ ...p, [txId]: categoriaId ? { categoria_id: categoriaId, categoria_nome: categoriaNome } : null }));
  };

  const creaEAssegnaSugg = async (txId, nome, importo) => {
    const tipo = importo >= 0 ? 'ENTRATA' : 'USCITA';
    const r = await api.post('/banca/categorie', { nome, tipo, colore: '#3B82F6' });
    setCategorie(prev => [...prev, r.data]);
    aggiornaSceltaSugg(txId, r.data.id, r.data.nome);
  };

  // ── RESET CATEGORIE ─────────────────────────────────────
  const resetTutteCategorie = async () => {
    if (!window.confirm('Azzerare la categoria di TUTTE le transazioni? Questa operazione è irreversibile.')) return;
    try {
      const r = await api.post('/banca/transazioni/reset-categorie', {});
      alert(`${r.data.azzerate} transazioni azzerate.`);
      loadTransazioni();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  // ── WIZARD ───────────────────────────────────────────────
  const apriWizard = async () => {
    setShowWizard(true);
    setShowAutocat(false);
    setWizardLoading(true);
    try {
      const params = new URLSearchParams({ anno: filtroAnno });
      if (wizardMese) params.set('mese', wizardMese);
      const r = await api.get('/banca/transazioni/wizard?' + params.toString());
      const gruppi = r.data.gruppi.slice(0, 80);
      setWizardGruppi(gruppi);
      setWizardAperti(new Set());
      setWizardEsclusi(new Set());
      const scelte = {};
      gruppi.forEach(g => {
        if (g.categoria_id_suggerita) scelte[g.chiave] = { categoria_id: g.categoria_id_suggerita, categoria_nome: g.categoria_nome_suggerita };
      });
      setWizardScelte(scelte);
    } catch(e) { console.error(e); }
    finally { setWizardLoading(false); }
  };

  const applicaWizard = async () => {
    const applicazioni = wizardGruppi
      .filter(g => wizardScelte[g.chiave]?.categoria_id)
      .map(g => ({ ids: g.ids.filter(id => !wizardEsclusi.has(id)), categoria_id: wizardScelte[g.chiave].categoria_id }))
      .filter(a => a.ids.length > 0);
    if (!applicazioni.length) return;
    setApplyingWizard(true);
    try {
      await api.post('/banca/transazioni/applica-wizard', { applicazioni });
      setShowWizard(false);
      setWizardGruppi([]);
      setWizardScelte({});
      loadTransazioni();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setApplyingWizard(false); }
  };

  // ✓ sul singolo gruppo: applica solo quello (senza le righe tolte), gli altri restano
  const applicaUnGruppoWizard = async (g) => {
    const v = wizardScelte[g.chiave];
    const ids = g.ids.filter(id => !wizardEsclusi.has(id));
    if (!v?.categoria_id || !ids.length) return;
    setApplicandoUna(g.chiave);
    try {
      await api.post('/banca/transazioni/applica-wizard', { applicazioni: [{ ids, categoria_id: v.categoria_id }] });
      setWizardGruppi(p => p.filter(x => x.chiave !== g.chiave));
      setWizardScelte(p => { const n = { ...p }; delete n[g.chiave]; return n; });
      loadTransazioni();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setApplicandoUna(null); }
  };

  const creaEAssegnaWizard = async (chiave, nome, tipo) => {
    const r = await api.post('/banca/categorie', { nome, tipo, colore: '#3B82F6' });
    setCategorie(prev => [...prev, r.data]);
    setWizardScelte(p => ({ ...p, [chiave]: { categoria_id: r.data.id, categoria_nome: r.data.nome } }));
  };

  // ── SELEZIONE / ELIMINA ──────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [eliminando, setEliminando]   = useState(false);

  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = () => {
    if (visibili.length > 0 && selectedIds.size === visibili.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(visibili.map(t => t.id)));
  };

  const eliminaSelezionate = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare le ${selectedIds.size} transazioni selezionate? L'operazione non è reversibile.`)) return;
    setEliminando(true);
    try {
      for (const id of selectedIds) {
        await api.delete(`/banca/transazioni/${id}`);
      }
      setTransazioni(prev => prev.filter(t => !selectedIds.has(t.id)));
      setSelectedIds(new Set());
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setEliminando(false); }
  };

  const cassaManuale = casse.find(c => String(c.id) === String(nuovaTx.cassa_id));
  const manualeContanti = cassaManuale?.tipo === 'CONTANTI';
  const vuotaTx = { data:'', operazione:'', dettagli:'', importo:'', contabilizzazione:'', conto:'', categoria_id:'', cassa_id:'', note:'', movimentato_da:'' };
  // nomi già usati nei contanti, come suggerimento per "chi ha movimentato"
  const nomiMovimentatori = [...new Set(transazioni.map(t => t.movimentato_da).filter(Boolean))].sort();

  const salvaTxManuale = async () => {
    if (!nuovaTx.data) return alert('Inserisci la data');
    if (!nuovaTx.operazione.trim()) return alert(manualeContanti ? 'Scrivi per cosa è stato movimentato il denaro' : 'Inserisci il tipo operazione');
    if (manualeContanti && !nuovaTx.movimentato_da.trim()) return alert('Indica chi ha dato o preso il denaro');
    if (!nuovaTx.importo || isNaN(parseFloat(nuovaTx.importo))) return alert('Importo non valido');
    setSalvandoManuale(true);
    try {
      const r = await api.post('/banca/transazioni', {
        data:              nuovaTx.data,
        operazione:        nuovaTx.operazione,
        dettagli:          nuovaTx.dettagli   || null,
        importo:           parseFloat(nuovaTx.importo),
        contabilizzazione: nuovaTx.contabilizzazione || null,
        conto:             nuovaTx.conto       || null,
        categoria_id:      nuovaTx.categoria_id || null,
        cassa_id:          nuovaTx.cassa_id    || null,
        note:              nuovaTx.note        || null,
        movimentato_da:    nuovaTx.movimentato_da || null,
      });
      setShowManuale(false);
      setNuovaTx(vuotaTx);
      loadTransazioni();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvandoManuale(false); }
  };

  // Accredito Satispay: in banca è un bonifico cumulativo che non dice chi ha pagato.
  // Il dettaglio serve solo a saperlo; in contabilità resta una riga sola come nell'estratto.
  const isAccreditoSatispay = (t) => t.importo > 0 && /satis\s?pay/i.test(`${t.operazione} ${t.dettagli || ''}`);
  const toggleSatispay = async (id) => {
    if (satDettaglio[id]) { setSatDettaglio(p => { const n = { ...p }; delete n[id]; return n; }); return; }
    setSatDettaglio(p => ({ ...p, [id]: { loading: true } }));
    try {
      const r = await api.get(`/satispay/accredito/${id}`);
      setSatDettaglio(p => ({ ...p, [id]: { data: r.data } }));
    } catch(e) { setSatDettaglio(p => ({ ...p, [id]: { error: e.response?.data?.error || e.message } })); }
  };

  const eliminaFileImportato = async (anno, fileName, nTx) => {
    if (!window.confirm(`Eliminare il file "${fileName}" e le sue ${nTx} transazioni dal database?`)) return;
    try {
      await api.delete(`/banca/file/${anno}/${encodeURIComponent(fileName)}/import`);
      loadFileImportati();
      loadTransazioni();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  // ── CATEGORIA SINGOLA ────────────────────────────────────
  const aggiornaCategoria = async (id, categoria_id) => {
    setSavingId(p => ({ ...p, [id]: true }));
    try {
      const r = await api.put(`/banca/transazioni/${id}`, { categoria_id: categoria_id || null });
      setTransazioni(prev => prev.map(t => t.id === id ? { ...t, categoria_id: r.data.categoria_id, categoria: r.data.categoria } : t));
    } catch(e) {
      alert('Errore aggiornamento: ' + (e.response?.data?.error || e.message));
    } finally { setSavingId(p => ({ ...p, [id]: false })); }
  };

  // ── VALIDAZIONE (blocca da modifiche accidentali) ──────────
  const validaTransazione = async (id) => {
    setSavingId(p => ({ ...p, [id]: true }));
    try {
      const r = await api.put(`/banca/transazioni/${id}`, { stato: 'VALIDATO' });
      setTransazioni(prev => prev.map(t => t.id === id ? { ...t, stato: r.data.stato } : t));
    } catch(e) {
      alert('Errore validazione: ' + (e.response?.data?.error || e.message));
    } finally { setSavingId(p => ({ ...p, [id]: false })); }
  };

  const sbloccaTransazione = async (id) => {
    setSavingId(p => ({ ...p, [id]: true }));
    try {
      const r = await api.put(`/banca/transazioni/${id}`, { stato: 'DA_VALIDARE' });
      setTransazioni(prev => prev.map(t => t.id === id ? { ...t, stato: r.data.stato } : t));
    } catch(e) {
      alert('Errore sblocco: ' + (e.response?.data?.error || e.message));
    } finally { setSavingId(p => ({ ...p, [id]: false })); }
  };

  const creaEAssegnaCategoria = async (txId, nome, importo) => {
    const tipo = importo >= 0 ? 'ENTRATA' : 'USCITA';
    const r = await api.post('/banca/categorie', { nome, tipo, colore: '#3B82F6' });
    setCategorie(prev => [...prev, r.data]);
    await aggiornaCategoria(txId, r.data.id);
  };

  const salvaNote = async (id) => {
    try {
      await api.put(`/banca/transazioni/${id}`, { note: noteTemp || null });
      setTransazioni(prev => prev.map(t => t.id === id ? { ...t, note: noteTemp || null } : t));
    } catch(e) {
      alert('Errore salvataggio nota: ' + (e.response?.data?.error || e.message));
    } finally { setEditingNoteId(null); }
  };

  // ── EXPORT ──────────────────────────────────────────────
  const esporta = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `/api/banca/export/${filtroAnno}`;
      if (filtroMese) url += `?mese=${filtroMese}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Errore export');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filtroMese
        ? `Movimenti_${filtroAnno}_${String(filtroMese).padStart(2,'0')}.xlsx`
        : `Movimenti_${filtroAnno}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch(e) { alert('Errore export: ' + e.message); }
  };

  const toggleOrder = (campo) => {
    if (orderBy === campo) setOrderDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setOrderBy(campo); setOrderDir('desc'); }
  };

  const creaCategoria = async (e) => {
    e.preventDefault();
    await api.post('/banca/categorie', newCat);
    setShowAddCat(false);
    setNewCat({ nome:'', tipo:'ENTRATA', centro_id:'', colore:'#3B82F6', parole_chiave:'' });
    loadBase();
  };

  const salvaCategoria = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/banca/categorie/${editCat.id}`, { nome: editCat.nome.trim(), centro_id: editCat.centro_id || null, parole_chiave: editCat.parole_chiave.trim() || null });
      setEditCat(null);
      loadBase();
    } catch(err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };

  const eliminaCategoria = async () => {
    if (!window.confirm(`Eliminare la categoria "${editCat.nome}"?`)) return;
    try {
      await api.delete(`/banca/categorie/${editCat.id}`);
      setEditCat(null);
      loadBase();
    } catch(err) { alert(err.response?.data?.error || err.message); }
  };

  const creaCassa = async (e) => {
    e.preventDefault();
    await api.post('/banca/casse', newCassa);
    setShowAddCassa(false);
    setNewCassa({ nome:'', tipo:'BANCA', iban:'', colore:'#3B82F6' });
    loadBase();
  };

  const salvaSaldoIniziale = async (cassa_id) => {
    const { saldo_iniziale, data_saldo_iniziale } = editSaldo[cassa_id] || {};
    try {
      await api.put(`/banca/casse/${cassa_id}`, { saldo_iniziale: parseFloat(saldo_iniziale), data_saldo_iniziale });
      setEditSaldo(p => { const n = { ...p }; delete n[cassa_id]; return n; });
      loadBase();
      // Aggiorna il saldo calcolato se questa è la cassa selezionata
      if (!filtroCassa || +filtroCassa === cassa_id) caricaSaldoCalcolato(cassa_id);
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const caricaSaldoCalcolato = async (cassa_id) => {
    try {
      const r = await api.get(`/banca/casse/${cassa_id}/saldo`);
      setSaldoCalcolato(r.data);
    } catch(e) { setSaldoCalcolato(null); }
  };

  // Quando cambio filtro cassa, carica il saldo calcolato se la cassa ha un saldo iniziale
  useEffect(() => {
    if (filtroCassa) {
      const cassa = casse.find(c => c.id === +filtroCassa);
      if (cassa?.saldo_iniziale != null) caricaSaldoCalcolato(+filtroCassa);
      else setSaldoCalcolato(null);
    } else {
      // Cerca prima cassa bancaria con saldo iniziale impostato
      const prima = casse.find(c => c.tipo === 'BANCA' && c.saldo_iniziale != null);
      if (prima) caricaSaldoCalcolato(prima.id);
      else setSaldoCalcolato(null);
    }
  }, [filtroCassa, casse]); // eslint-disable-line

  // Ricerca: parole tutte presenti in descrizione, dettagli, note, categoria, chi ha movimentato o importo.
  // I filtri sopra restano sul server; ricerca e pagine lavorano su tutti i movimenti filtrati.
  const paroleCerca = cerca.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const visibili = paroleCerca.length === 0 ? transazioni : transazioni.filter(t => {
    const imp = Math.abs(+t.importo || 0);
    const testo = `${t.operazione || ''} ${t.dettagli || ''} ${t.note || ''} ${t.categoria?.nome || ''} ${t.movimentato_da || ''} ${imp.toFixed(2)} ${imp.toFixed(2).replace('.', ',')} ${imp.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`.toLowerCase();
    return paroleCerca.every(w => testo.includes(w));
  });
  const PER_PAGINA = 50;
  const nPagine    = Math.max(1, Math.ceil(visibili.length / PER_PAGINA));
  const paginaOk   = Math.min(pagina, nPagine - 1);
  const righePagina = visibili.slice(paginaOk * PER_PAGINA, (paginaOk + 1) * PER_PAGINA);
  const totEntrate = paroleCerca.length ? visibili.filter(t => t.importo > 0).reduce((a, t) => a + t.importo, 0) : kpi.entrate;
  const totUscite  = paroleCerca.length ? visibili.filter(t => t.importo < 0).reduce((a, t) => a + t.importo, 0) : kpi.uscite;
  const daCateg    = transazioni.filter(t => !t.categoria_id).length;

  const loadEstrattiAnno = async () => {
    try { const r = await api.get(`/banca/estratti/${estrattiAnno}`); setEstrattiData(r.data); }
    catch(e) { console.error(e); }
  };

  const uploadEstratto = async (trimestre, file) => {
    setUploadingQ(trimestre);
    try {
      const b64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const r = await api.post(`/banca/estratti/${estrattiAnno}/${trimestre}`, { base64: b64 });
      const l = r.data?.lettura;
      if (l && !l.ok) alert(`Q${trimestre} caricato, ma non riesco a leggerlo bene: ${l.errore}. Il controllo con la banca salterà questo trimestre.`);
      setEstrattomsg(l?.ok ? `Q${trimestre} caricato e letto: ${l.movimenti.length} movimenti` : `Q${trimestre} caricato`);
      setTimeout(() => setEstrattomsg(''), 4000);
      loadEstrattiAnno();
      setVerificaBanca(v => v + 1);
    } catch(e) { alert('Errore caricamento: ' + (e.response?.data?.error || e.message)); }
    finally { setUploadingQ(null); }
  };

  const deleteEstratto = async (trimestre) => {
    await api.delete(`/banca/estratti/${estrattiAnno}/${trimestre}`);
    loadEstrattiAnno();
  };

  const openEstratto = async (trimestre) => {
    try {
      const r = await api.get(`/banca/estratti/${estrattiAnno}/${trimestre}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch(e) { console.error(e); }
  };

  const colH = (campo, label) => (
    <th style={{ ...s.th, cursor:'pointer' }} onClick={() => toggleOrder(campo)}>
      <span style={{ display:'flex', alignItems:'center', gap:'4px' }}>
        {label} <ArrowUpDown size={10} color={orderBy===campo?'#F4600C':'var(--text-dim)'}/>
        {orderBy===campo && <span style={{ color:'#F4600C', fontSize: 'var(--font-size-xs)' }}>{orderDir==='desc'?'↓':'↑'}</span>}
      </span>
    </th>
  );

  return (
    <div>
      {/* HEADER */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px' }}>
        <div>
          <h1 style={s.title}>MOVIMENTI CONTO BANCARIO</h1>
          <p style={s.sub}>
            {transazioni.length} movimenti
            {daCateg > 0 && <span style={{ color:'#F59E0B' }}> · {daCateg} da categorizzare</span>}
          </p>
        </div>
        <div className="allinea-sinistra" style={{ display:'flex', gap:'8px', flexWrap:'wrap', justifyContent:'flex-end' }}>
          {daCateg > 0 && (
            <>
              <button style={{ ...s.btnWarn, background:'#EF444415', color:'#EF4444', border:'1px solid #EF444430' }}
                onClick={resetTutteCategorie} title="Azzera tutte le categorie">
                <X size={13}/> RESET CATEGORIE
              </button>
              <button style={s.btnWarn} onClick={apriWizard}>
                <Wand2 size={13}/> WIZARD
              </button>
              <button style={s.btnWarn} onClick={() => apriAutocat(false)} disabled={autocatLoading}>
                <Wand2 size={13}/> AUTOCAT {autocatLoading && <RefreshCw size={11} style={{ marginLeft:'4px' }}/>}
              </button>
            </>
          )}
          <button style={s.btnSec} onClick={esporta} title="Esporta xlsx per commercialista">
            <FileDown size={13}/> ESPORTA
          </button>
          <button style={s.btnSec} onClick={() => { setShowManuale(v => !v); setShowImport(false); }}>
            <Plus size={13}/> INSERISCI MANUALE
          </button>
          <button style={s.btnPri} onClick={() => { setPreview(null); setShowImport(v => !v); setShowManuale(false); }}>
            <Upload size={13}/> IMPORTA FILE
          </button>
        </div>
      </div>

      {/* ── MODAL INSERIMENTO MANUALE ── */}
      {showManuale && (
        <div style={s.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={s.cardTitle}><Plus size={14} style={{ marginRight:'6px' }}/> {manualeContanti ? 'MOVIMENTO IN CONTANTI' : 'INSERISCI TRANSAZIONE MANUALE'}</div>
            <button style={s.iconBtn} onClick={() => setShowManuale(false)}><X size={14}/></button>
          </div>

          {/* Riga 1: conto, data, importo */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
            <div>
              <label style={s.label}>CASSA / CONTO</label>
              <select style={s.inp} value={nuovaTx.cassa_id} onChange={e => setNuovaTx(p => ({ ...p, cassa_id: e.target.value }))}>
                <option value="">— nessuno —</option>
                {casse.filter(c => c.tipo !== 'CONTANTI' || String(c.id) === String(nuovaTx.cassa_id)).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>DATA *</label>
              <input type="date" style={s.inp} value={nuovaTx.data} onChange={e => setNuovaTx(p => ({ ...p, data: e.target.value }))}/>
            </div>
            <div>
              <label style={s.label}>IMPORTO * (- uscita, + entrata)</label>
              <input type="number" step="0.01" style={s.inp} placeholder={manualeContanti ? 'es. 120 oppure -35.50' : 'es. -643.20'} value={nuovaTx.importo} onChange={e => setNuovaTx(p => ({ ...p, importo: e.target.value }))}/>
            </div>
          </div>

          {manualeContanti ? (
            <>
              {/* Contanti: niente estratto conto, quindi ogni riga deve spiegarsi da sola */}
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'10px', marginBottom:'10px' }}>
                <div>
                  <label style={s.label}>PER COSA *</label>
                  <input type="text" style={s.inp} placeholder="es. Incasso iscrizioni torneo 12/10, acquisto acqua per il bar…" value={nuovaTx.operazione} onChange={e => setNuovaTx(p => ({ ...p, operazione: e.target.value }))}/>
                </div>
                <div>
                  <label style={s.label}>CHI HA DATO / PRESO IL DENARO *</label>
                  <input type="text" style={s.inp} list="movimentatori" placeholder="es. Marco (allenatore)" value={nuovaTx.movimentato_da} onChange={e => setNuovaTx(p => ({ ...p, movimentato_da: e.target.value }))}/>
                  <datalist id="movimentatori">{nomiMovimentatori.map(n => <option key={n} value={n}/>)}</datalist>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:'10px', marginBottom:'14px' }}>
                <div>
                  <label style={s.label}>CATEGORIA</label>
                  <select style={s.inp} value={nuovaTx.categoria_id} onChange={e => setNuovaTx(p => ({ ...p, categoria_id: e.target.value }))}>
                    <option value="">— nessuna —</option>
                    {categorie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>NOTE</label>
                  <input type="text" style={s.inp} placeholder="Dettagli in più (facoltativo)" value={nuovaTx.note} onChange={e => setNuovaTx(p => ({ ...p, note: e.target.value }))}/>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Riga 2: operazione, dettagli */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:'10px', marginBottom:'10px' }}>
                <div>
                  <label style={s.label}>TIPO OPERAZIONE *</label>
                  <input type="text" style={s.inp} placeholder="es. COMMISSIONI, INTERESSI, BONIFICO…" value={nuovaTx.operazione} onChange={e => setNuovaTx(p => ({ ...p, operazione: e.target.value }))}/>
                </div>
                <div>
                  <label style={s.label}>DETTAGLI</label>
                  <input type="text" style={s.inp} placeholder="Descrizione libera" value={nuovaTx.dettagli} onChange={e => setNuovaTx(p => ({ ...p, dettagli: e.target.value }))}/>
                </div>
              </div>

              {/* Riga 3: data valuta, conto, categoria, note */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 2fr', gap:'10px', marginBottom:'14px' }}>
                <div>
                  <label style={s.label}>DATA VALUTA</label>
                  <input type="date" style={s.inp} value={nuovaTx.contabilizzazione} onChange={e => setNuovaTx(p => ({ ...p, contabilizzazione: e.target.value }))}/>
                </div>
                <div>
                  <label style={s.label}>IBAN</label>
                  <input type="text" style={s.inp} placeholder="es. IT60 X054 2811…" value={nuovaTx.conto} onChange={e => setNuovaTx(p => ({ ...p, conto: e.target.value }))}/>
                </div>
                <div>
                  <label style={s.label}>CATEGORIA</label>
                  <select style={s.inp} value={nuovaTx.categoria_id} onChange={e => setNuovaTx(p => ({ ...p, categoria_id: e.target.value }))}>
                    <option value="">— nessuna —</option>
                    {categorie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>NOTE</label>
                  <input type="text" style={s.inp} placeholder="Note libere" value={nuovaTx.note} onChange={e => setNuovaTx(p => ({ ...p, note: e.target.value }))}/>
                </div>
              </div>
            </>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
            <button style={s.btnSec} onClick={() => setShowManuale(false)}>ANNULLA</button>
            <button style={s.btnPri} onClick={salvaTxManuale} disabled={salvandoManuale}>
              {salvandoManuale ? <RefreshCw size={13}/> : <Check size={13}/>}
              {salvandoManuale ? 'SALVATAGGIO…' : 'SALVA'}
            </button>
          </div>
        </div>
      )}

      {/* ── MODAL IMPORT: step 1 ── */}
      {showImport && !preview && (
        <div style={s.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={s.cardTitle}>IMPORTA ESTRATTO BANCARIO</div>
            <button style={s.iconBtn} onClick={() => setShowImport(false)}><X size={14}/></button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'12px' }}>
            <div>
              <label style={s.label}>ANNO</label>
              <select style={s.inp} value={importAnno} onChange={e => setImportAnno(+e.target.value)}>
                {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>CONTO / CASSA</label>
              <select style={s.inp} value={importCassa} onChange={e => setImportCassa(e.target.value)}>
                <option value="">— seleziona —</option>
                {casse.filter(c => c.tipo === 'BANCA').map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" multiple style={{ display:'none' }} onChange={handleFileSelect}/>
              <button style={{ ...s.btnPri, justifyContent:'center' }} disabled={importing} onClick={() => fileRef.current.click()}>
                {importing ? <RefreshCw size={13}/> : <Upload size={13}/>}
                {importing ? 'ANALISI...' : 'SELEZIONA FILE XLSX'}
              </button>
            </div>
          </div>
          <p style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginBottom:0 }}>
            Formato: export Intesa San Paolo. Puoi selezionare <strong style={{ color:'#aaa' }}>più file contemporaneamente</strong> — il sistema li analizza tutti, unisce le transazioni e salta i duplicati anche tra file diversi.
          </p>
        </div>
      )}

      {/* ── MODAL IMPORT: step 2 — anteprima ── */}
      {preview && (
        <div style={s.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <div style={s.cardTitle}>
              ANTEPRIMA IMPORT{preview?.nFiles > 1 ? ` — ${preview.nFiles} FILE` : ''}
            </div>
            <button style={s.iconBtn} onClick={() => setPreview(null)}><X size={14}/></button>
          </div>
          <div style={{ display:'flex', gap:'10px', marginBottom:'16px', flexWrap:'wrap' }}>
            <div style={{ ...s.kpiSmall, flex:1, minWidth:'120px', flexDirection:'column', alignItems:'flex-start', gap:'3px' }}>
              <span style={{ fontSize: 'var(--font-size-title)', fontWeight:'800', fontFamily:"'Barlow Condensed'", color:'#22C55E', lineHeight:1 }}>{preview.nuove.filter(r => !r._aggiorna).length}</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color:'#555', letterSpacing:'1px' }}>NUOVE DA IMPORTARE</span>
            </div>
            {preview.nuove.some(r => r._aggiorna) && (
              <div style={{ ...s.kpiSmall, flex:1, minWidth:'120px', flexDirection:'column', alignItems:'flex-start', gap:'3px' }} title="Movimenti già nel gestionale (in attesa, o bonifici a cui la banca ha aggiunto il BIC): si aggiornano invece di essere caricati due volte">
                <span style={{ fontSize: 'var(--font-size-title)', fontWeight:'800', fontFamily:"'Barlow Condensed'", color:'#3B82F6', lineHeight:1 }}>{preview.nuove.filter(r => r._aggiorna).length}</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color:'#555', letterSpacing:'1px' }}>GIÀ CARICATI → AGGIORNATI</span>
              </div>
            )}
            <div style={{ ...s.kpiSmall, flex:1, minWidth:'120px', flexDirection:'column', alignItems:'flex-start', gap:'3px' }}>
              <span style={{ fontSize: 'var(--font-size-title)', fontWeight:'800', fontFamily:"'Barlow Condensed'", color:'#F59E0B', lineHeight:1 }}>{preview.duplicate.length}</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color:'#555', letterSpacing:'1px' }}>GIÀ PRESENTI — SKIP</span>
            </div>
            {preview.saltate?.length > 0 && (
              <div style={{ ...s.kpiSmall, flex:1, minWidth:'120px', flexDirection:'column', alignItems:'flex-start', gap:'3px', border:'1px solid #EF444440' }}>
                <span style={{ fontSize: 'var(--font-size-title)', fontWeight:'800', fontFamily:"'Barlow Condensed'", color:'#EF4444', lineHeight:1 }}>{preview.saltate.length}</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color:'#EF4444', letterSpacing:'1px' }}>RIGHE NON LETTE</span>
              </div>
            )}
          </div>

          {preview.saltate?.length > 0 && (
            <details style={{ marginBottom:'14px', border:'1px solid #EF444430', borderRadius:'8px', overflow:'hidden' }}>
              <summary style={{ padding:'8px 12px', fontSize: 'var(--font-size-xs)', fontWeight:'700', color:'#EF4444', cursor:'pointer', background:'#EF444410' }}>
                ⚠ {preview.saltate.length} righe saltate — clicca per dettagli
              </summary>
              <div style={{ maxHeight:'150px', overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'var(--bg-input,#1A1A1A)' }}>
                    <th style={{ ...s.th, width:'50px' }}>RIGA</th>
                    <th style={{ ...s.th }}>MOTIVO</th>
                    <th style={{ ...s.th }}>RAW</th>
                  </tr></thead>
                  <tbody>
                    {preview.saltate.map((r, i) => (
                      <tr key={i} style={{ borderBottom:'1px solid var(--border,#1E1E1E)' }}>
                        <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', color:'#F59E0B' }}>{r.riga}</td>
                        <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', color:'#EF4444' }}>{r.motivo}</td>
                        <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', color:'#555', maxWidth:'200px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.raw}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {preview.nuove.length > 0 && (
            <div style={{ maxHeight:'220px', overflowY:'auto', marginBottom:'14px', border:'1px solid var(--border,#1E1E1E)', borderRadius:'8px' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ background:'var(--bg-input,#1A1A1A)', position:'sticky', top:0 }}>
                  <th style={s.th}>DATA</th><th style={s.th}>IMPORTO</th><th style={s.th}>OPERAZIONE</th><th style={s.th}>DETTAGLI</th>
                </tr></thead>
                <tbody>
                  {preview.nuove.slice(0,100).map((r,i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border,#1E1E1E)' }}>
                      <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', whiteSpace:'nowrap' }}>{r.data}</td>
                      <td style={{ ...s.td, textAlign:'right', whiteSpace:'nowrap' }}>
                        <span style={{ fontFamily:"'Barlow Condensed'", fontWeight:'800', fontSize: 'var(--font-size-sm)', color: parseFloat(r.importo)>0?'#22C55E':'#EF4444' }}>
                          {parseFloat(r.importo)>0?'+':''}{fmt(r.importo)}
                        </span>
                      </td>
                      <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', maxWidth:'160px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {r._aggiorna && <span title={r._aggiorna.completa ? 'Stesso bonifico già caricato: la banca ha completato il testo (BIC), si aggiorna quello che c\'è' : `Aggiorna il movimento in attesa del ${r._aggiorna.data}`} style={{ ...s.inAttesa, background:'#3B82F615', color:'#3B82F6', borderColor:'#3B82F640' }}>{r._aggiorna.completa ? 'COMPLETA' : 'AGGIORNA'}</span>}
                        {r._sostituisce && <span title={`Sostituisce il movimento in attesa del ${r._sostituisce.data} (${fmt(r._sostituisce.importo)}), che la banca ha registrato in due righe: ne prende categoria e nota`} style={{ ...s.inAttesa, background:'#8B5CF615', color:'#8B5CF6', borderColor:'#8B5CF640' }}>SOSTITUISCE IN ATTESA</span>}
                        {r._parte_di && <span title="Seconda riga del movimento in attesa registrato in due righe" style={{ ...s.inAttesa, background:'#8B5CF615', color:'#8B5CF6', borderColor:'#8B5CF640' }}>+ IN ATTESA</span>}
                        {!r._aggiorna && r.contabilizzazione === 'NO' && <span style={s.inAttesa}>IN ATTESA</span>}
                        {r.operazione}
                      </td>
                      <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', color:'#666', maxWidth:'180px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.dettagli}</td>
                    </tr>
                  ))}
                  {preview.nuove.length > 100 && (
                    <tr><td colSpan={4} style={{ ...s.td, textAlign:'center', color:'#555', fontSize: 'var(--font-size-xs)' }}>... e altre {preview.nuove.length - 100}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button style={s.btnPri} disabled={importing || preview.nuove.length === 0} onClick={confermaImport}>
              {importing ? <RefreshCw size={13}/> : <Check size={13}/>}
              {importing ? 'IMPORTAZIONE...' : `IMPORTA ${preview.nuove.length} MOVIMENTI`}
            </button>
            <button style={s.btnSec} onClick={() => { setPreview(null); setShowImport(false); }}><X size={13}/> ANNULLA</button>
            {preview.nuove.length === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color:'#F59E0B', marginLeft:'8px' }}>Tutte le transazioni sono già presenti.</span>}
          </div>
        </div>
      )}

      {/* ── MODAL AUTO-CATEGORIZZAZIONE ── */}
      {showAutocat && (
        <div style={{ ...s.card, borderColor:'#8B5CF640' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={{ ...s.cardTitle, color:'#8B5CF6' }}>
              <Wand2 size={16} style={{ marginRight:'8px' }}/> CATEGORIZZAZIONE SMART
            </div>
            <button style={s.iconBtn} onClick={() => setShowAutocat(false)}><X size={14}/></button>
          </div>

          {autocatLoading && (
            <div style={{ textAlign:'center', padding:'30px', color:'#555' }}>
              <RefreshCw size={20} color="#8B5CF6" style={{ marginBottom:'8px' }}/><br/>Analisi in corso...
            </div>
          )}

          {!autocatLoading && suggerimenti.length === 0 && (
            <p style={{ color:'#555', fontSize: 'var(--font-size-sm)' }}>Tutte le transazioni sono già categorizzate.</p>
          )}

          {!autocatLoading && suggerimenti.length > 0 && (() => {
            const conMatch    = suggerimenti.filter(s => s.categoria_id).length;
            const senzaMatch  = suggerimenti.filter(s => !s.categoria_id).length;
            const nSelezionate = Object.values(scelteSugg).filter(v => v?.categoria_id).length;
            return (
              <>
                <div style={{ display:'flex', gap:'16px', marginBottom:'12px', fontSize: 'var(--font-size-xs)' }}>
                  <span style={{ color:'#22C55E', fontWeight:'700' }}>{conMatch} suggeriti automaticamente</span>
                  {senzaMatch > 0 && <span style={{ color:'#F59E0B', fontWeight:'700' }}>{senzaMatch} da assegnare manualmente</span>}
                  <span style={{ color:'#555' }}>· {suggerimenti.length} totali senza categoria</span>
                </div>

                <div style={{ maxHeight:'420px', overflowY:'auto', marginBottom:'14px', border:'1px solid var(--border,#1E1E1E)', borderRadius:'8px' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
                    <thead style={{ position:'sticky', top:0, zIndex:10, background:'var(--bg-card,#141414)' }}>
                      <tr>
                        <th style={{ ...s.th, width:'40%' }}>DESCRIZIONE</th>
                        <th style={{ ...s.th, width:'16%' }}>DA / A</th>
                        <th style={{ ...s.th, width:'10%', textAlign:'right' }}>IMPORTO</th>
                        <th style={{ ...s.th, width:'7%', textAlign:'center' }} title="KEY = parole chiave della categoria · MEM = stessa descrizione già categorizzata in passato">FONTE</th>
                        <th style={{ ...s.th, width:'27%' }}>CATEGORIA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggerimenti.map(sg => {
                        const scelta = scelteSugg[sg.id];
                        const txFake = { id: sg.id, categoria_id: scelta?.categoria_id || null, categoria: scelta ? { nome: scelta.categoria_nome } : null, importo: sg.importo };
                        return (
                          <tr key={sg.id} style={{ borderBottom:'1px solid var(--border,#1E1E1E)', background: scelta?.categoria_id ? '#22C55E04' : 'transparent' }}>
                            <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', fontWeight:'600', color:'var(--text)', wordBreak:'break-word', lineHeight:'1.4' }} title={sg.dettagli || ''}>
                              {sg.descrizione || <span style={{ color:'#555', fontWeight:'400' }}>(senza descrizione)</span>}
                            </td>
                            <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', color:'#777', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={sg.operazione}>
                              {sg.controparte || sg.operazione}
                            </td>
                            <td style={{ ...s.td, textAlign:'right', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-sm)', fontWeight:'800', color: sg.importo>0?'#22C55E':'#EF4444', whiteSpace:'nowrap' }}>
                              {sg.importo>0?'+':''}{fmt(sg.importo)}
                            </td>
                            <td style={{ ...s.td, textAlign:'center' }}>
                              {sg.metodo && (
                                <span title={sg.motivo || ''} style={{ fontSize: 'var(--font-size-xs)', padding:'2px 5px', borderRadius:'4px', cursor:'help',
                                  background: sg.metodo==='keyword'?'#8B5CF620':'#22C55E20',
                                  color: sg.metodo==='keyword'?'#8B5CF6':'#22C55E', fontWeight:'700' }}>
                                  {sg.metodo==='keyword'?'KEY':sg.metodo==='parole'?'IMPARATO':'MEM'}
                                </span>
                              )}
                            </td>
                            <td style={{ ...s.td, padding:'4px 8px' }}>
                              <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                              <div style={{ flex:1, minWidth:0 }}>
                              <CategoriaCombobox
                                tx={txFake}
                                categorie={categorie.filter(c => c.tipo === (sg.importo >= 0 ? 'ENTRATA' : 'USCITA') || c.tipo === 'ENTRAMBI')}
                                onAssign={(_, catId) => {
                                  const cat = categorie.find(c => c.id === catId);
                                  aggiornaSceltaSugg(sg.id, catId, cat?.nome || null);
                                }}
                                onCreaEAssegna={(_, nome) => creaEAssegnaSugg(sg.id, nome, sg.importo)}
                              />
                              </div>
                              <button style={{ ...s.iconBtn, color:'#22C55E', border:'1px solid #22C55E40', flexShrink:0 }} title="Applica solo questa riga"
                                  disabled={!(scelta?.categoria_id) || applicandoUna !== null} onClick={() => applicaUnaSugg(sg.id)}>
                                  {applicandoUna === sg.id ? <RefreshCw size={13}/> : <Check size={13}/>}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                  <button style={{ ...s.btnPri, background:'#8B5CF6' }}
                    disabled={applyingAutocat || nSelezionate === 0}
                    onClick={applicaAutocat}>
                    {applyingAutocat ? <RefreshCw size={13}/> : <Check size={13}/>}
                    APPLICA {nSelezionate} CATEGORIE
                  </button>
                  <button style={s.btnSec} onClick={() => setShowAutocat(false)}><X size={13}/> CHIUDI</button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ── WIZARD CATEGORIZZAZIONE ── */}
      {showWizard && (
        <div style={{ ...s.card, borderColor:'#F4600C40' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={{ ...s.cardTitle }}><Wand2 size={16} style={{ marginRight:'8px' }}/> WIZARD CATEGORIZZAZIONE</div>
            <button style={s.iconBtn} onClick={() => setShowWizard(false)}><X size={14}/></button>
          </div>

          {/* Filtro mese */}
          <div style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'12px' }}>
            <label style={s.label}>FILTRA PER MESE</label>
            <select style={{ ...s.filterSel, width:'140px' }} value={wizardMese} onChange={e => setWizardMese(e.target.value)}>
              <option value="">Tutti i mesi</option>
              {MESI.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <button style={{ ...s.btnSec, padding:'6px 12px', fontSize: 'var(--font-size-xs)' }} onClick={apriWizard}>
              <RefreshCw size={11}/> AGGIORNA
            </button>
            <span style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>Prima i gruppi con categoria suggerita, poi per descrizione · max 80 gruppi per sessione</span>
          </div>

          {wizardLoading && (
            <div style={{ textAlign:'center', padding:'30px', color:'#555' }}>
              <RefreshCw size={20} color="#F4600C" style={{ marginBottom:'8px' }}/><br/>Analisi gruppi in corso...
            </div>
          )}

          {!wizardLoading && wizardGruppi.length === 0 && (
            <p style={{ color:'#555', fontSize: 'var(--font-size-sm)' }}>Nessuna transazione senza categoria{wizardMese ? ` per ${MESI[wizardMese]}` : ''}.</p>
          )}

          {!wizardLoading && wizardGruppi.length > 0 && (() => {
            const nAssegnati = Object.values(wizardScelte).filter(v => v?.categoria_id).length;
            return (
              <>
                <div style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginBottom:'10px' }}>
                  <span style={{ color:'#F4600C', fontWeight:'700' }}>{wizardGruppi.length} gruppi</span> distinti ·{' '}
                  <span style={{ color:'#22C55E', fontWeight:'700' }}>{nAssegnati} assegnati</span>
                  {' '}· Assegna una categoria e premi ✓ per applicare solo quel gruppo, oppure APPLICA A TUTTE in fondo
                </div>

                <div style={{ maxHeight:'450px', overflowY:'auto', marginBottom:'14px', border:'1px solid var(--border,#1E1E1E)', borderRadius:'8px' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
                    <thead style={{ position:'sticky', top:0, zIndex:10, background:'var(--bg-card,#141414)' }}>
                      <tr>
                        <th style={{ ...s.th, width:'20%' }}>GRUPPO</th>
                        <th style={{ ...s.th, width:'10%', textAlign:'center' }}>N. TX</th>
                        <th style={{ ...s.th, width:'10%', textAlign:'right' }}>TOTALE</th>
                        <th style={{ ...s.th, width:'35%' }}>ESEMPI DI DESCRIZIONE</th>
                        <th style={{ ...s.th, width:'25%' }}>CATEGORIA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wizardGruppi.map(g => {
                        const scelta = wizardScelte[g.chiave];
                        const txFake = { id: g.chiave, categoria_id: scelta?.categoria_id || null, categoria: scelta ? { nome: scelta.categoria_nome } : null, importo: g.importo_totale };
                        const aperto = wizardAperti.has(g.chiave);
                        const nInclusi = g.ids.filter(id => !wizardEsclusi.has(id)).length;
                        const toggleAperto = () => setWizardAperti(prev => { const n = new Set(prev); n.has(g.chiave) ? n.delete(g.chiave) : n.add(g.chiave); return n; });
                        const toggleEscluso = (id) => setWizardEsclusi(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
                        return (
                          <React.Fragment key={g.chiave}>
                          <tr style={{ borderBottom: aperto ? 'none' : '1px solid var(--border,#1E1E1E)', background: scelta?.categoria_id ? '#22C55E04' : 'transparent' }}>
                            <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', fontWeight:'700', overflow:'hidden' }} title={g.operazione}>
                              <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.operazione}</div>
                              <div style={{ fontSize:'10px', fontWeight:'600', marginTop:'2px', color: g.suggerito ? '#8B5CF6' : '#666' }}>
                                {g.suggerito ? (g.metodo === 'descrizione' ? 'SUGGERITO · descrizione già vista' : g.metodo === 'parole' ? 'SUGGERITO · imparato dalle tue scelte' : 'SUGGERITO · parole chiave') : 'stessa descrizione'}
                              </div>
                            </td>
                            <td style={{ ...s.td, textAlign:'center', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-sm)', fontWeight:'800', color:'#888' }}>
                              <button onClick={toggleAperto} title="Vedi le singole transazioni ed escludi quelle sbagliate"
                                style={{ background:'none', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'6px', color:'#aaa', cursor:'pointer', padding:'2px 8px', fontFamily:"'Barlow Condensed'", fontWeight:'800', fontSize:'var(--font-size-sm)' }}>
                                {nInclusi < g.n_transazioni ? `${nInclusi}/${g.n_transazioni}` : g.n_transazioni} {aperto ? '▴' : '▾'}
                              </button>
                            </td>
                            <td style={{ ...s.td, textAlign:'right', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-sm)', fontWeight:'800', color: g.importo_totale>=0?'#22C55E':'#EF4444', whiteSpace:'nowrap' }}>
                              {g.importo_totale>=0?'+':''}{fmt(g.importo_totale)}
                            </td>
                            <td style={{ ...s.td, fontSize: 'var(--font-size-xs)', color:'#aaa', wordBreak:'break-word', lineHeight:'1.5' }}>
                              {g.esempi_dettagli.map((d,i) => <div key={i}>{d}</div>)}
                            </td>
                            <td style={{ ...s.td, padding:'4px 8px' }}>
                              <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                              <div style={{ flex:1, minWidth:0 }}>
                              <CategoriaCombobox
                                tx={txFake}
                                categorie={categorie.filter(c => c.tipo === g.tipo || c.tipo === 'ENTRAMBI')}
                                onAssign={(_, catId) => {
                                  const cat = categorie.find(c => c.id === catId);
                                  setWizardScelte(p => ({ ...p, [g.chiave]: catId ? { categoria_id: catId, categoria_nome: cat?.nome } : null }));
                                }}
                                onCreaEAssegna={(_, nome) => creaEAssegnaWizard(g.chiave, nome, g.tipo)}
                              />
                              </div>
                              <button style={{ ...s.iconBtn, color:'#22C55E', border:'1px solid #22C55E40', flexShrink:0 }} title="Applica solo questo gruppo"
                                  disabled={!(scelta?.categoria_id && nInclusi > 0) || applicandoUna !== null} onClick={() => applicaUnGruppoWizard(g)}>
                                  {applicandoUna === g.chiave ? <RefreshCw size={13}/> : <Check size={13}/>}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {aperto && (
                            <tr style={{ borderBottom:'1px solid var(--border,#1E1E1E)' }}>
                              <td colSpan={5} style={{ padding:'0 10px 10px 24px' }}>
                                <div style={{ fontSize:'10px', color:'#666', margin:'2px 0 6px' }}>Togli la spunta alle transazioni che non c'entrano: resteranno senza categoria.</div>
                                {g.transazioni.map(t => (
                                  <label key={t.id} style={{ display:'grid', gridTemplateColumns:'18px 80px 1fr 90px', gap:'8px', alignItems:'center', fontSize:'var(--font-size-xs)', padding:'3px 0', color: wizardEsclusi.has(t.id) ? '#555' : '#ccc', cursor:'pointer' }}>
                                    <input type="checkbox" checked={!wizardEsclusi.has(t.id)} onChange={() => toggleEscluso(t.id)}/>
                                    <span>{new Date(t.data).toLocaleDateString('it-IT')}</span>
                                    <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.motivo || ''}>
                                      <b>{t.descrizione || '(senza descrizione)'}</b>{t.controparte && t.controparte !== t.descrizione ? <span style={{ color:'#777' }}> · {t.controparte}</span> : null}{t.motivo ? <span style={{ color:'#8B5CF6' }}> · {t.motivo}</span> : null}
                                    </span>
                                    <span style={{ textAlign:'right', fontFamily:"'Barlow Condensed'", fontWeight:'800', color: t.importo>=0?'#22C55E':'#EF4444' }}>{t.importo>=0?'+':''}{fmt(t.importo)}</span>
                                  </label>
                                ))}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                  <button style={s.btnPri} disabled={applyingWizard || nAssegnati === 0} onClick={applicaWizard}>
                    {applyingWizard ? <RefreshCw size={13}/> : <Check size={13}/>}
                    APPLICA A TUTTE ({nAssegnati} gruppi)
                  </button>
                  <button style={s.btnSec} onClick={() => setShowWizard(false)}><X size={13}/> CHIUDI</button>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* TABS */}
      <div className="schede-scorrevoli" style={{ display:'flex', gap:'4px', marginBottom:'20px', borderBottom:'1px solid var(--border,#1E1E1E)' }}>
        {[['movimenti','MOVIMENTI'],['satispay','SATISPAY'],['contanti','CONTANTI'],['files','FILE IMPORTATI'],['estratti','ESTRATTI CONTO'],['categorie','CATEGORIE'],['casse','CASSE E CONTI']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'10px 18px', background:'transparent', border:'none', borderBottom: tab===id?'2px solid #F4600C':'2px solid transparent',
              color: tab===id?'var(--text)':'#666', fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', cursor:'pointer', marginBottom:'-1px' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── TAB MOVIMENTI ── */}
      {tab === 'movimenti' && (
        <div>
          {/* KPI */}
          <div style={{ display:'flex', gap:'10px', marginBottom: saldoCalcolato ? '10px' : '14px', flexWrap:'wrap' }}>
            <div style={s.kpiSmall}><span style={{ color:'#22C55E', fontWeight:'800', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>+{fmt(totEntrate)}</span><span style={{ color:'#555', fontSize: 'var(--font-size-xs)', marginLeft:'6px' }}>ENTRATE{paroleCerca.length > 0 && ' DEI TROVATI'}</span></div>
            <div style={s.kpiSmall}><span style={{ color:'#EF4444', fontWeight:'800', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>-{fmt(Math.abs(totUscite))}</span><span style={{ color:'#555', fontSize: 'var(--font-size-xs)', marginLeft:'6px' }}>USCITE{paroleCerca.length > 0 && ' DEI TROVATI'}</span></div>
            <div style={s.kpiSmall}><span style={{ color:(totEntrate+totUscite)>=0?'#22C55E':'#EF4444', fontWeight:'800', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>{fmt(totEntrate+totUscite)}</span><span style={{ color:'#555', fontSize: 'var(--font-size-xs)', marginLeft:'6px' }}>SALDO PERIODO{paroleCerca.length > 0 && ' DEI TROVATI'}</span></div>
            {paroleCerca.length > 0 && (
              <div style={{ ...s.kpiSmall, border:'1px dashed var(--border2)', cursor:'pointer' }} onClick={() => setCerca('')} title="Svuota la ricerca per tornare ai totali del periodo">
                <span style={{ color:'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>Totali solo dei movimenti trovati con "{cerca.trim()}" · mostra tutto</span>
              </div>
            )}
            {daCateg > 0 && (
              <div style={{ ...s.kpiSmall, border:'1px solid #F59E0B40', cursor:'pointer' }} onClick={() => apriAutocat(false)}>
                <AlertCircle size={13} color="#F59E0B"/>
                <span style={{ color:'#F59E0B', fontWeight:'700', fontSize: 'var(--font-size-sm)', marginLeft:'6px' }}>{daCateg} da categorizzare</span>
              </div>
            )}
            {saldoFineTrimestre && (
              <div style={{ ...s.kpiSmall, border:'1px solid #3B82F640', background:'#3B82F608', marginLeft:'auto' }}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                  <span style={{ color:'#3B82F6', fontWeight:'800', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)' }}>
                    {saldoFineTrimestre.saldo_calcolato >= 0 ? '+' : ''}{fmt(saldoFineTrimestre.saldo_calcolato)}
                  </span>
                  <span style={{ color:'#555', fontSize: 'var(--font-size-xs)', letterSpacing:'1px' }}>
                    SALDO AL {new Date(fineTrimestre(filtroAnno, +filtroTrimestre)+'T12:00:00').toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',year:'numeric'})}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* BOX VERIFICA SALDO */}
          {saldoCalcolato && (
            <SaldoVerifica saldoCalcolato={saldoCalcolato} />
          )}
          <ControlloBanca aggiorna={verificaBanca} onCambiato={() => { loadTransazioni(); if (saldoCalcolato) caricaSaldoCalcolato(saldoCalcolato.cassa_id); }} />

          {/* Filtri */}
          <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap', alignItems:'center' }}>
            <select style={s.filterSel} value={filtroAnno} onChange={e => setFiltroAnno(+e.target.value)}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {/* Bottoni trimestre */}
            {[1,2,3,4].map(q => (
              <button key={q}
                onClick={() => { setFiltroTrimestre(filtroTrimestre===String(q)?'':String(q)); setFiltroMese(''); }}
                style={{ padding:'7px 12px', borderRadius:'6px', border:'none', cursor:'pointer', fontWeight:'800', fontSize: 'var(--font-size-xs)', letterSpacing:'1px',
                  background: filtroTrimestre===String(q) ? '#3B82F6' : 'var(--bg-input)',
                  color:      filtroTrimestre===String(q) ? '#fff'    : '#555',
                  outline: filtroTrimestre===String(q) ? '1px solid #3B82F6' : '1px solid var(--border2)',
                }}>Q{q}</button>
            ))}
            <select style={s.filterSel} value={filtroMese} onChange={e => { setFiltroMese(e.target.value); setFiltroTrimestre(''); }}>
              <option value="">Tutti i mesi</option>
              {MESI.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <select style={s.filterSel} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
              <option value="">Entrate + Uscite</option>
              <option value="ENTRATA">Solo Entrate</option>
              <option value="USCITA">Solo Uscite</option>
            </select>
            <select style={s.filterSel} value={filtroCat} onChange={e => setFiltroCat(e.target.value)}>
              <option value="">Tutte le categorie</option>
              <option value="__NONE__">⚠ Senza categoria</option>
              {categorie.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            <select style={s.filterSel} value={filtroCassa} onChange={e => setFiltroCassa(e.target.value)}>
              <option value="">Tutti i conti</option>
              {casse.filter(c => c.tipo !== 'CONTANTI').map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            <span style={{ marginLeft:'auto', fontSize: 'var(--font-size-xs)', color:'#555' }}>{transazioni.length} movimenti</span>
          </div>
          <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ position:'relative', flex:'1 1 260px', maxWidth:'480px' }}>
              <Search size={14} style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
              <input style={{ ...s.filterSel, width:'100%', paddingLeft:'30px', paddingRight: cerca ? '30px' : undefined, boxSizing:'border-box' }}
                placeholder="Cerca: nome, causale, nota, importo…" value={cerca} onChange={e => setCerca(e.target.value)}/>
              {cerca && <button onClick={() => setCerca('')} title="Svuota la ricerca"
                style={{ position:'absolute', right:'6px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', display:'flex', padding:'2px' }}><X size={14}/></button>}
            </div>
            {paroleCerca.length > 0 && <span style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>{visibili.length} trovati su {transazioni.length}</span>}
          </div>

          {/* Barra azione selezione */}
          {selectedIds.size > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:'12px', background:'#EF444415', border:'1px solid #EF444430', borderRadius:'8px', padding:'10px 14px', marginBottom:'10px' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', color:'#EF4444' }}>{selectedIds.size} selezionate</span>
              <button style={{ ...s.btnSec, padding:'6px 12px', fontSize: 'var(--font-size-xs)', color:'#EF4444', borderColor:'#EF444440' }}
                disabled={eliminando}
                onClick={eliminaSelezionate}>
                {eliminando ? <RefreshCw size={12}/> : <Trash2 size={12}/>}
                {eliminando ? 'ELIMINAZIONE...' : 'ELIMINA SELEZIONATE'}
              </button>
              <button style={{ ...s.btnSec, padding:'6px 12px', fontSize: 'var(--font-size-xs)', marginLeft:'auto' }}
                onClick={() => setSelectedIds(new Set())}>
                <X size={12}/> DESELEZIONA
              </button>
            </div>
          )}

          {/* Tabella */}
          <div style={{ overflowX:'auto' }}>
            <table className="tabella-schede" style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
              <colgroup>
                <col style={{ width:'36px' }}/>
                <col style={{ width:'105px' }}/>
                <col style={{ width:'130px' }}/>
                <col style={{ width:'auto' }}/>
                <col style={{ width:'210px' }}/>
                <col style={{ width:'64px' }}/>
              </colgroup>
              <thead>
                <tr style={{ background:'var(--bg-input,#1A1A1A)' }}>
                  <th style={{ ...s.th, textAlign:'center' }}>
                    <input type="checkbox"
                      style={{ accentColor:'#EF4444', cursor:'pointer' }}
                      checked={visibili.length > 0 && selectedIds.size === visibili.length}
                      title={`Seleziona tutti i ${visibili.length} movimenti trovati (anche nelle altre pagine)`}
                      onChange={toggleSelectAll}/>
                  </th>
                  {colH('data','DATA')}
                  {colH('importo','IMPORTO')}
                  <th style={s.th}>DESCRIZIONE</th>
                  <th style={s.th}>CATEGORIA</th>
                  <th style={s.th}>STATO</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} style={{ textAlign:'center', padding:'30px', color:'#555' }}>Caricamento...</td></tr>}
                {!loading && transazioni.length > 0 && visibili.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:'40px', color:'#555' }}>Nessun movimento contiene "{cerca}".</td></tr>
                )}
                {!loading && transazioni.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:'60px', color:'#555' }}>
                    Nessuna transazione. Usa "IMPORTA FILE" per caricare l'estratto bancario.
                  </td></tr>
                )}
                {righePagina.map(t => {
                  const isEntrata = t.importo > 0;
                  const saving = !!savingId[t.id];
                  const sel = selectedIds.has(t.id);
                  const isEditingNote = editingNoteId === t.id;
                  return (
                    <tr key={t.id}
                      style={{ borderBottom:'1px solid var(--border,#1E1E1E)', background: sel ? '#EF444408' : 'transparent' }}
                      onMouseEnter={e=>{ if(!sel) e.currentTarget.style.background='var(--bg-input,#1A1A1A)'; }}
                      onMouseLeave={e=>{ if(!sel) e.currentTarget.style.background='transparent'; }}>
                      <td className="td-via" style={{ ...s.td, textAlign:'center' }}>
                        <input type="checkbox"
                          style={{ accentColor:'#EF4444', cursor:'pointer' }}
                          checked={sel}
                          onChange={() => toggleSelect(t.id)}/>
                      </td>
                      <td className="td-meta" style={{ ...s.td, whiteSpace:'nowrap' }}>
                        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'600' }}>
                          {new Date(t.data).toLocaleDateString('it-IT',{day:'2-digit',month:'short',year:'numeric'})}
                        </div>
                        {t.cassa && <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>{t.cassa.nome}</div>}
                      </td>
                      <td className="td-meta" style={{ ...s.td, textAlign:'right', whiteSpace:'nowrap', paddingRight:'16px' }}>
                        <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color: isEntrata?'#22C55E':'#EF4444' }}>
                          {isEntrata?'+':''}{fmt(t.importo)}
                        </span>
                      </td>
                      <td className="td-testo" style={{ ...s.td, overflow:'hidden' }}>
                        <div style={{ fontWeight:'600', fontSize: 'var(--font-size-xs)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {t.contabilizzazione === 'NO' && <span title="La banca non l'ha ancora contabilizzato: è già nel saldo, al prossimo import si aggiorna da solo" style={s.inAttesa}>IN ATTESA</span>}
                          {t.operazione}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-muted,#888)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.dettagli}</div>
                        {t.movimentato_da && (
                          <div style={{ fontSize: 'var(--font-size-xs)', color:'#F59E0B', marginTop:'2px' }}>
                            Movimentato da <b>{t.movimentato_da}</b>{t.registrato_da && <span style={{ color:'#666' }}> · registrato da {t.registrato_da}</span>}
                          </div>
                        )}
                        {isAccreditoSatispay(t) && (
                          <div style={{ marginTop:'4px' }}>
                            <span onClick={() => toggleSatispay(t.id)} style={{ fontSize: 'var(--font-size-xs)', color:'#EF4444', cursor:'pointer', fontWeight:'700' }}>
                              {satDettaglio[t.id] ? '▾' : '▸'} chi ha pagato (Satispay)
                            </span>
                            {satDettaglio[t.id]?.loading && <span style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginLeft:'6px' }}>caricamento…</span>}
                            {satDettaglio[t.id]?.error && <div style={{ fontSize: 'var(--font-size-xs)', color:'#EF4444' }}>{satDettaglio[t.id].error}</div>}
                            {satDettaglio[t.id]?.data && (() => { const d = satDettaglio[t.id].data; return (
                              <div style={{ marginTop:'4px', padding:'6px 8px', background:'var(--bg-input,#1A1A1A)', borderRadius:'6px', fontSize: 'var(--font-size-xs)' }}>
                                {!d.matched && (
                                  <div style={{ color:'#F59E0B', marginBottom:'4px' }}>
                                    Abbinamento incerto: i pagamenti trovati fanno {fmt(d.totale_netto)} netti, l'accredito è {fmt(d.importo_banca)}.
                                  </div>
                                )}
                                {d.pagamenti.length === 0 ? <div style={{ color:'#555' }}>Nessun pagamento Satispay trovato nei giorni precedenti.</div> : d.pagamenti.map(p => (
                                  <div key={p.id} style={{ display:'flex', justifyContent:'space-between', gap:'10px' }}>
                                    <span>{new Date(p.data).toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit'})} · {p.mittente}</span>
                                    <span style={{ fontWeight:'700' }}>{fmt(p.importo)}</span>
                                  </div>
                                ))}
                                {d.pagamenti.length > 0 && (
                                  <div style={{ color:'#666', marginTop:'4px', borderTop:'1px solid var(--border,#1E1E1E)', paddingTop:'3px' }}>
                                    Lordo {fmt(d.totale_lordo)} − commissione {fmt(d.commissione)} = {fmt(d.totale_netto)}
                                  </div>
                                )}
                              </div>
                            ); })()}
                          </div>
                        )}
                        {/* Nota editabile */}
                        {isEditingNote ? (
                          <div style={{ marginTop:'5px', display:'flex', gap:'4px', alignItems:'flex-start' }}>
                            <textarea
                              autoFocus
                              rows={2}
                              style={{ ...s.inp, fontSize: 'var(--font-size-xs)', padding:'5px 8px', resize:'vertical', flex:1 }}
                              value={noteTemp}
                              onChange={e => setNoteTemp(e.target.value)}
                              placeholder="es. Alessandro €50 quota / Giulia €25 campo 3..."
                              onKeyDown={e => { if (e.key === 'Escape') setEditingNoteId(null); }}
                            />
                            <button style={{ ...s.iconBtn, color:'#22C55E', padding:'4px 6px' }} onClick={() => salvaNote(t.id)}><Check size={12}/></button>
                            <button style={{ ...s.iconBtn, padding:'4px 6px' }} onClick={() => setEditingNoteId(null)}><X size={12}/></button>
                          </div>
                        ) : t.note ? (
                          <div style={{ marginTop:'4px', display:'flex', alignItems:'flex-start', gap:'6px', cursor:'pointer' }}
                            onClick={() => { setEditingNoteId(t.id); setNoteTemp(t.note || ''); }}>
                            <div style={{ fontSize: 'var(--font-size-xs)', color:'#3B82F6', fontStyle:'italic', lineHeight:'1.4', flex:1 }}>{t.note}</div>
                            <Edit2 size={10} color="#3B82F6" style={{ flexShrink:0, marginTop:'2px' }}/>
                          </div>
                        ) : (
                          <div style={{ marginTop:'3px', opacity:0.3, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'4px' }}
                            onClick={() => { setEditingNoteId(t.id); setNoteTemp(''); }}>
                            <Edit2 size={10} color="#555"/>
                            <span style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>aggiungi nota</span>
                          </div>
                        )}
                      </td>
                      <td className="td-largo" data-label="Categoria" style={s.td}>
                        {t.stato === 'VALIDATO' ? (
                          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                            <Lock size={10} color="#22C55E" style={{ flexShrink:0 }}/>
                            <span style={{ fontSize: 'var(--font-size-xs)', color:'#22C55E', fontWeight:'700' }}>
                              {t.categoria?.nome || '— nessuna categoria —'}
                            </span>
                          </div>
                        ) : (
                          <CategoriaCombobox
                            tx={t}
                            categorie={categorie}
                            onAssign={aggiornaCategoria}
                            onCreaEAssegna={creaEAssegnaCategoria}
                          />
                        )}
                        {t.categoria && (
                          <div style={{ fontSize:'10px', color: t.categoria.giroconto ? '#64748B' : (t.categoria.centro ? '#8B5CF6' : '#555'), marginTop:'3px' }}>
                            {t.categoria.giroconto ? 'giroconto · escluso dal bilancio' : (t.categoria.centro ? t.categoria.centro.nome : 'categoria senza centro')}
                          </div>
                        )}
                      </td>
                      <td className="td-stretto" style={{ ...s.td, textAlign:'center' }}>
                        {saving && <RefreshCw size={12} color="#F59E0B"/>}
                        {!saving && t.stato === 'VALIDATO' && (
                          <button title="Sblocca per modificare la categoria" onClick={() => sbloccaTransazione(t.id)}
                            style={{ ...s.iconBtn, color:'#22C55E' }}>
                            <Lock size={12}/>
                          </button>
                        )}
                        {!saving && t.stato !== 'VALIDATO' && (
                          <button title={t.categoria_id ? "Valida: blocca da modifiche accidentali" : "Assegna prima una categoria"}
                            disabled={!t.categoria_id}
                            onClick={() => validaTransazione(t.id)}
                            style={{ ...s.iconBtn, color: t.categoria_id ? '#3B82F6' : 'var(--text-dim)', cursor: t.categoria_id ? 'pointer' : 'not-allowed' }}>
                            <Unlock size={12}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {nPagine > 1 && (() => {
            const vai = (n) => { setPagina(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
            const btn = (dis) => ({ ...s.btnSec, padding:'6px 12px', fontSize:'var(--font-size-xs)', opacity: dis ? 0.4 : 1, cursor: dis ? 'default' : 'pointer' });
            return (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', flexWrap:'wrap', padding:'14px 0' }}>
                <button style={btn(paginaOk === 0)} disabled={paginaOk === 0} onClick={() => vai(0)}>«</button>
                <button style={btn(paginaOk === 0)} disabled={paginaOk === 0} onClick={() => vai(paginaOk - 1)}>‹ Indietro</button>
                <select style={s.filterSel} value={paginaOk} onChange={e => vai(+e.target.value)}>
                  {Array.from({ length: nPagine }, (_, i) => (
                    <option key={i} value={i}>Pagina {i + 1} di {nPagine} · righe {i * PER_PAGINA + 1}–{Math.min((i + 1) * PER_PAGINA, visibili.length)}</option>
                  ))}
                </select>
                <button style={btn(paginaOk >= nPagine - 1)} disabled={paginaOk >= nPagine - 1} onClick={() => vai(paginaOk + 1)}>Avanti ›</button>
                <button style={btn(paginaOk >= nPagine - 1)} disabled={paginaOk >= nPagine - 1} onClick={() => vai(nPagine - 1)}>»</button>
                <span style={{ fontSize:'var(--font-size-xs)', color:'var(--text-muted)' }}>{visibili.length} movimenti</span>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── TAB SATISPAY ── */}
      {tab === 'contanti' && <Contanti casse={casse} categorie={categorie} s={s}/>}

      {tab === 'satispay' && (
        <div>
          {/* KPI */}
          {!loadingSatispay && satispayPayments.length > 0 && (() => {
            const totale = satispayPayments.reduce((a, p) => a + p.amount_unit / 100, 0);
            const oggi = new Date().toISOString().slice(0, 10);
            const meseCorrente = oggi.slice(0, 7);
            const delMese = satispayPayments.filter(p => p.insert_date?.slice(0, 7) === meseCorrente);
            const totaleMese = delMese.reduce((a, p) => a + p.amount_unit / 100, 0);
            return (
              <div style={{ display:'flex', gap:'10px', marginBottom:'16px', flexWrap:'wrap' }}>
                <div style={s.kpiSmall}><span style={{ color:'#22C55E', fontWeight:'800', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>€{totale.toFixed(2)}</span><span style={{ color:'#555', fontSize: 'var(--font-size-xs)', marginLeft:'6px' }}>TOTALE VISIBILE</span></div>
                <div style={s.kpiSmall}><span style={{ color:'#22C55E', fontWeight:'800', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>€{totaleMese.toFixed(2)}</span><span style={{ color:'#555', fontSize: 'var(--font-size-xs)', marginLeft:'6px' }}>MESE CORRENTE</span></div>
                <div style={s.kpiSmall}><span style={{ color: 'var(--text)', fontWeight:'800', fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)' }}>{satispayPayments.length}</span><span style={{ color:'#555', fontSize: 'var(--font-size-xs)', marginLeft:'6px' }}>PAGAMENTI</span></div>
              </div>
            );
          })()}

          {/* Legenda dot */}
          <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginBottom:'12px', display:'flex', gap:'16px', flexWrap:'wrap' }}>
            <span><span style={{ display:'inline-block', width:'8px', height:'8px', borderRadius:'50%', background:'#22C55E', marginRight:'5px' }}/>Versamento bancario riconciliato</span>
            <span><span style={{ display:'inline-block', width:'8px', height:'8px', borderRadius:'50%', background:'#F59E0B', marginRight:'5px' }}/>Versamento trovato, importo non coincide</span>
            <span><span style={{ display:'inline-block', width:'8px', height:'8px', borderRadius:'50%', background:'#EF4444', marginRight:'5px' }}/>Nessun versamento bancario trovato</span>
            <span style={{ color:'var(--text-dim)' }}>(i puntini appaiono sui movimenti bancari in entrata con "SATISPAY" nella descrizione)</span>
          </div>

          {satispayError && <div style={{ color:'#EF4444', padding:'20px', textAlign:'center' }}>{satispayError}</div>}
          {loadingSatispay && <div style={{ color:'#555', padding:'30px', textAlign:'center' }}>Caricamento pagamenti Satispay...</div>}

          {!loadingSatispay && !satispayError && (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'var(--bg-input,#1A1A1A)' }}>
                    <th style={s.th}>DATA</th>
                    <th style={s.th}>MITTENTE</th>
                    <th style={{ ...s.th, textAlign:'right' }}>IMPORTO</th>
                    <th style={s.th}>STATO</th>
                    <th style={{ ...s.th, fontFamily:'monospace', fontSize: 'var(--font-size-xs)' }}>ID</th>
                  </tr>
                </thead>
                <tbody>
                  {satispayPayments.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign:'center', padding:'40px', color:'#555' }}>Nessun pagamento trovato.</td></tr>
                  )}
                  {satispayPayments.map(p => {
                    const importo = p.amount_unit / 100;
                    const data = new Date(p.insert_date).toLocaleDateString('it-IT', { day:'2-digit', month:'short', year:'numeric' });
                    return (
                      <tr key={p.id} style={{ borderBottom:'1px solid var(--border,#1E1E1E)' }}
                        onMouseEnter={e=>e.currentTarget.style.background='var(--bg-input,#1A1A1A)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={{ ...s.td, whiteSpace:'nowrap', fontSize: 'var(--font-size-xs)' }}>{data}</td>
                        <td style={{ ...s.td, fontWeight:'600', fontSize: 'var(--font-size-sm)' }}>{p.sender?.name || '—'}</td>
                        <td style={{ ...s.td, textAlign:'right', whiteSpace:'nowrap' }}>
                          <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#22C55E' }}>+€{importo.toFixed(2)}</span>
                        </td>
                        <td style={s.td}>
                          <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', padding:'2px 8px', borderRadius:'4px', color:'#22C55E', background:'#22C55E20' }}>
                            {p.status}
                          </span>
                        </td>
                        <td style={{ ...s.td, fontFamily:'monospace', fontSize: 'var(--font-size-xs)', color:'var(--text-dim)', maxWidth:'120px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.id}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB FILE IMPORTATI ── */}
      {tab === 'files' && (
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginBottom:'14px' }}>
            Ogni riga corrisponde a un file xlsx importato. Eliminando un file vengono rimosse anche le sue transazioni dal database.
          </div>
          {fileImportati.length === 0 && (
            <div style={{ textAlign:'center', padding:'40px', color:'#555' }}>
              <FileSpreadsheet size={32} color="var(--text-dim)" style={{ marginBottom:'10px' }}/>
              <p>Nessun file importato.</p>
            </div>
          )}
          {Object.entries(
            fileImportati.reduce((g,f) => { const a=f.anno; if(!g[a])g[a]=[]; g[a].push(f); return g; }, {})
          ).sort(([a],[b]) => +b - +a).map(([anno, lista]) => (
            <div key={anno} style={{ marginBottom:'20px' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#F4600C', marginBottom:'8px', paddingBottom:'6px', borderBottom:'1px solid var(--border,#1E1E1E)' }}>{anno}</div>
              {lista.map(f => (
                <div key={f.fileName} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px', marginBottom:'6px' }}>
                  <FileSpreadsheet size={18} color="#22C55E" style={{ flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:'600', fontSize: 'var(--font-size-sm)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.fileName}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginTop:'2px', display:'flex', gap:'12px' }}>
                      <span style={{ color:'#22C55E', fontWeight:'700' }}>{f.n_transazioni} transazioni</span>
                      {f.importo_netto !== null && (
                        <span style={{ color: f.importo_netto >= 0 ? '#22C55E' : '#EF4444', fontWeight:'700' }}>
                          {f.importo_netto >= 0 ? '+' : ''}{fmt(f.importo_netto)} netto
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    style={{ ...s.btnSec, padding:'6px 10px', fontSize: 'var(--font-size-xs)', color:'#EF4444', borderColor:'#EF444430' }}
                    onClick={() => eliminaFileImportato(f.anno, f.fileName, f.n_transazioni)}
                    title="Elimina file e transazioni dal DB">
                    <Trash2 size={12}/> ELIMINA
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── TAB ESTRATTI CONTO ── */}
      {tab === 'estratti' && (
        <div>
          {estrattoMsg && (
            <div style={{ position:'fixed',top:'20px',right:'20px',background:'#22C55E',color:'#fff',padding:'12px 20px',borderRadius:'8px',fontWeight:'700',fontSize: 'var(--font-size-sm)',zIndex:9999,display:'flex',alignItems:'center',gap:'8px' }}>
              <Check size={14}/> {estrattoMsg}
            </div>
          )}

          <ControlloBanca aggiorna={verificaBanca} onCambiato={() => setVerificaBanca(v => v + 1)} />

          {/* Selettore anno */}
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'24px' }}>
            <button onClick={() => setEstrattiAnno(a => a-1)} style={{ background:'var(--bg-input)',border:'1px solid var(--border2)',borderRadius:'6px',padding:'6px 12px',color: 'var(--text)',cursor:'pointer',fontWeight:'700' }}>‹</button>
            <span style={{ fontFamily:"'Barlow Condensed'",fontSize: 'var(--font-size-xl)',fontWeight:'800',letterSpacing:'2px',minWidth:'60px',textAlign:'center' }}>{estrattiAnno}</span>
            <button onClick={() => setEstrattiAnno(a => a+1)} style={{ background:'var(--bg-input)',border:'1px solid var(--border2)',borderRadius:'6px',padding:'6px 12px',color: 'var(--text)',cursor:'pointer',fontWeight:'700' }}>›</button>
          </div>

          {/* 4 card trimestrali */}
          <input ref={estrattoRef} type="file" accept="application/pdf" style={{ display:'none' }}/>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'16px' }}>
            {[1,2,3,4].map(q => {
              const mesiLabel = {1:'Gen – Mar',2:'Apr – Giu',3:'Lug – Set',4:'Ott – Dic'};
              const estratto  = estrattiData?.estratti?.find(e => e.trimestre === q);
              const totale    = estrattiData?.totali?.find(t => t.trimestre === q);
              const ent  = totale?.entrate || 0;
              const usc  = totale?.uscite  || 0;
              const sal  = ent + usc;
              const fmt2 = (n) => `€${Math.abs(n).toFixed(2)}`;
              const busy = uploadingQ === q;

              return (
                <div key={q} style={{ background:'var(--bg-card)', border:`1px solid ${estratto?'#3B82F640':'var(--border)'}`, borderRadius:'12px', padding:'20px' }}>
                  {/* Intestazione trimestre */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
                    <div>
                      <div style={{ fontFamily:"'Barlow Condensed'",fontSize: 'var(--font-size-xl)',fontWeight:'800',letterSpacing:'1px' }}>Q{q} — {estrattiAnno}</div>
                      <div style={{ fontSize: 'var(--font-size-xs)',color:'#555',marginTop:'2px' }}>{mesiLabel[q]}</div>
                    </div>
                    {estratto
                      ? <span style={{ background:'#3B82F620',color:'#3B82F6',border:'1px solid #3B82F640',borderRadius:'6px',padding:'3px 10px',fontSize: 'var(--font-size-xs)',fontWeight:'800' }}>PDF CARICATO</span>
                      : <span style={{ background:'var(--bg-input)',color:'#555',border:'1px solid var(--border2)',borderRadius:'6px',padding:'3px 10px',fontSize: 'var(--font-size-xs)',fontWeight:'700' }}>NESSUN PDF</span>
                    }
                  </div>

                  {/* Totali dal gestionale */}
                  <div style={{ background:'var(--bg-input)',borderRadius:'8px',padding:'12px',marginBottom:'14px' }}>
                    <div style={{ fontSize: 'var(--font-size-xs)',letterSpacing:'2px',fontWeight:'700',color:'var(--text-dim)',marginBottom:'8px' }}>TRANSAZIONI NEL GESTIONALE</div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize: 'var(--font-size-sm)' }}>
                      <span style={{ color:'#22C55E',fontWeight:'700' }}>+{fmt2(ent)}</span>
                      <span style={{ color:'#EF4444',fontWeight:'700' }}>-{fmt2(Math.abs(usc))}</span>
                      <span style={{ color: sal>=0?'#22C55E':'#EF4444', fontWeight:'800' }}>{sal>=0?'+':''}{fmt2(sal)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize: 'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'4px' }}>
                      <span>ENTRATE</span><span>USCITE</span><span>SALDO</span>
                    </div>
                    {totale?.count === 0 && <div style={{ fontSize: 'var(--font-size-xs)',color:'var(--text-dim)',marginTop:'6px',textAlign:'center' }}>Nessuna transazione</div>}
                  </div>

                  {/* Totali letti dal PDF (data contabile: possono differire di qualche movimento a cavallo dei trimestri) */}
                  {estratto?.riepilogo && (
                    <div style={{ background:'var(--bg-input)',borderRadius:'8px',padding:'12px',marginBottom:'14px' }}>
                      <div style={{ fontSize: 'var(--font-size-xs)',letterSpacing:'2px',fontWeight:'700',color:'var(--text-dim)',marginBottom:'8px' }}>DALL'ESTRATTO PDF</div>
                      {estratto.riepilogo.ok ? (<>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize: 'var(--font-size-sm)' }}>
                          <span style={{ color:'#22C55E',fontWeight:'700' }}>+{fmt2(estratto.riepilogo.tot_accrediti)}</span>
                          <span style={{ color:'#EF4444',fontWeight:'700' }}>-{fmt2(estratto.riepilogo.tot_addebiti)}</span>
                          <span style={{ fontWeight:'800' }}>{fmt2(estratto.riepilogo.saldo_finale)}</span>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize: 'var(--font-size-xs)', color:'var(--text-dim)', marginTop:'4px' }}>
                          <span>ACCREDITI</span><span>ADDEBITI</span><span>SALDO FINALE</span>
                        </div>
                      </>) : <div style={{ fontSize: 'var(--font-size-xs)', color:'#EF4444' }}>Non letto: {estratto.riepilogo.errore}</div>}
                    </div>
                  )}

                  {/* Azioni PDF */}
                  <div style={{ display:'flex', gap:'8px' }}>
                    {estratto ? (
                      <>
                        <button onClick={() => openEstratto(q)}
                          style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',background:'#3B82F620',color:'#3B82F6',border:'1px solid #3B82F640',borderRadius:'8px',padding:'9px',fontSize: 'var(--font-size-xs)',fontWeight:'700',cursor:'pointer' }}>
                          <FileText size={14}/> APRI PDF
                        </button>
                        <button onClick={() => deleteEstratto(q)}
                          style={{ background:'#EF444420',color:'#EF4444',border:'1px solid #EF444440',borderRadius:'8px',padding:'9px 12px',cursor:'pointer',display:'flex',alignItems:'center' }}>
                          <Trash2 size={14}/>
                        </button>
                      </>
                    ) : (
                      <button disabled={busy}
                        onClick={() => {
                          estrattoRef.current.onchange = (e) => {
                            if (e.target.files[0]) uploadEstratto(q, e.target.files[0]);
                            estrattoRef.current.value = '';
                          };
                          estrattoRef.current.click();
                        }}
                        style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',background:'#F4600C20',color:busy?'#888':'#F4600C',border:'1px solid #F4600C40',borderRadius:'8px',padding:'9px',fontSize: 'var(--font-size-xs)',fontWeight:'700',cursor:'pointer',opacity:busy?0.6:1 }}>
                        {busy ? <RefreshCw size={14} style={{ animation:'spin 1s linear infinite' }}/> : <Upload size={14}/>}
                        {busy ? 'CARICAMENTO...' : 'CARICA PDF'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB CATEGORIE ── */}
      {tab === 'categorie' && (() => {
        // Movimento → categoria → centro: le categorie si mostrano raggruppate per il centro a cui appartengono
        const gruppi = {};
        for (const c of categorie) {
          const k = c.giroconto ? '~giroconto' : (c.centro ? c.centro.nome : '~senza');
          (gruppi[k] = gruppi[k] || []).push(c);
        }
        const chiavi = Object.keys(gruppi).sort((a, b) => (a.startsWith('~') - b.startsWith('~')) || a.localeCompare(b));
        const titolo = (k) => k === '~giroconto' ? 'GIROCONTI (esclusi dal bilancio)' : k === '~senza' ? 'SENZA CENTRO' : k.toUpperCase();
        const selectCentro = (value, onChange) => (
          <select style={s.inp} value={value || ''} onChange={e => onChange(e.target.value)}>
            <option value="">— nessun centro —</option>
            {centri.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        );
        return (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={s.secTitle}>CATEGORIE CONTABILI</div>
            <button style={s.btnSec} onClick={()=>setShowAddCat(v=>!v)}><Plus size={13}/> NUOVA</button>
          </div>
          {showAddCat && (
            <form onSubmit={creaCategoria} style={{ background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'10px', padding:'14px', marginBottom:'12px', display:'grid', gridTemplateColumns:'2fr 1fr 1fr 60px 36px', gap:'8px', alignItems:'end' }}>
              <div><label style={s.label}>NOME *</label><input style={s.inp} value={newCat.nome} onChange={e=>setNewCat(p=>({...p,nome:e.target.value}))} required/></div>
              <div><label style={s.label}>TIPO</label><select style={s.inp} value={newCat.tipo} onChange={e=>setNewCat(p=>({...p,tipo:e.target.value}))}><option value="ENTRATA">Entrata</option><option value="USCITA">Uscita</option><option value="ENTRAMBI">Entrambi</option></select></div>
              <div><label style={s.label}>CENTRO</label>{selectCentro(newCat.centro_id, v=>setNewCat(p=>({...p,centro_id:v})))}</div>
              <div><label style={s.label}>COLORE</label><input type="color" style={{ ...s.inp, padding:'3px', height:'36px' }} value={newCat.colore} onChange={e=>setNewCat(p=>({...p,colore:e.target.value}))}/></div>
              <button type="submit" style={{ ...s.btnPri, padding:'8px' }}><Check size={13}/></button>
              <div style={{ gridColumn:'1 / -1' }}><label style={s.label}>PAROLE CHIAVE (separate da virgola)</label><input style={s.inp} value={newCat.parole_chiave} onChange={e=>setNewCat(p=>({...p,parole_chiave:e.target.value}))} placeholder="es. sesto, sesto san giovanni"/></div>
            </form>
          )}
          <div style={{ fontSize:'var(--font-size-xs)', color:'#666', marginBottom:'12px', lineHeight:'1.5' }}>
            Ogni movimento prende il <b>centro</b> dalla sua categoria: scegli qui a quale centro appartiene ogni categoria (i centri si creano da Cassa → Centri costo/ricavo).
            Clicca una categoria per modificarne nome, centro e <b>parole chiave</b>, o per eliminarla (solo se nessun movimento la usa).
            Le parole vengono cercate nella descrizione dei movimenti (per i bonifici la causale, non il nome di chi paga) (basta l'inizio della parola: "sesto" trova anche "Sestogiovedi") e servono a suggerire la categoria. Niente viene assegnato senza conferma.
          </div>
          {editCat && (
            <form onSubmit={salvaCategoria} style={{ background:'var(--bg-input,#1A1A1A)', border:'1px solid #8B5CF660', borderRadius:'10px', padding:'14px', marginBottom:'12px', display:'grid', gridTemplateColumns:'1fr 1fr 2fr 36px 36px 36px', gap:'8px', alignItems:'end' }}>
              <div><label style={s.label}>NOME</label><input style={s.inp} value={editCat.nome} onChange={e=>setEditCat(p=>({...p,nome:e.target.value}))} required/></div>
              <div><label style={s.label}>CENTRO</label>{editCat.giroconto ? <div style={{ ...s.inp, color:'#64748B' }}>giroconto</div> : selectCentro(editCat.centro_id, v=>setEditCat(p=>({...p,centro_id:v})))}</div>
              <div><label style={s.label}>PAROLE CHIAVE (separate da virgola)</label><input style={s.inp} autoFocus value={editCat.parole_chiave} onChange={e=>setEditCat(p=>({...p,parole_chiave:e.target.value}))} placeholder="es. robbiate, corso estivo"/></div>
              <button type="submit" style={{ ...s.btnPri, padding:'8px' }} title="Salva"><Check size={13}/></button>
              <button type="button" style={{ ...s.btnSec, padding:'8px', color:'#EF4444', borderColor:'#EF444430' }} title="Elimina categoria (solo se non usata da nessun movimento)" onClick={eliminaCategoria}><Trash2 size={13}/></button>
              <button type="button" style={{ ...s.btnSec, padding:'8px' }} title="Annulla" onClick={()=>setEditCat(null)}><X size={13}/></button>
            </form>
          )}
          {chiavi.map(k => (
            <div key={k} style={{ marginBottom:'14px' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color: k === '~senza' ? '#F59E0B' : '#555', marginBottom:'6px', paddingBottom:'4px', borderBottom:'1px solid var(--border,#1E1E1E)' }}>{titolo(k)}</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                {gruppi[k].map(c=>(
                  <div key={c.id} onClick={()=>setEditCat({ id:c.id, nome:c.nome, centro_id:c.centro_id ? String(c.centro_id) : '', giroconto:c.giroconto, parole_chiave:c.parole_chiave||'' })} title="Clicca per modificare, cambiare centro o eliminare"
                    style={{ cursor:'pointer', background:'var(--bg-input,#1A1A1A)', border:`1px solid ${editCat?.id===c.id ? '#8B5CF6' : 'var(--border2,#2A2A2A)'}`, borderRadius:'8px', padding:'7px 12px', maxWidth:'360px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                      <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:c.colore }}/>
                      <span style={{ fontSize: 'var(--font-size-sm)', fontWeight:'600' }}>{c.nome}</span>
                      <span style={{ fontSize: 'var(--font-size-xs)', color:c.tipo==='ENTRATA'?'#22C55E':c.tipo==='USCITA'?'#EF4444':'#888', fontWeight:'700' }}>{c.tipo}</span>
                    </div>
                    <div style={{ fontSize:'10px', color: c.parole_chiave ? '#8B5CF6' : '#555', marginTop:'3px', marginLeft:'15px' }}>
                      {c.parole_chiave || 'nessuna parola chiave'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {/* ── TAB CASSE ── */}
      {tab === 'casse' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={s.secTitle}>CASSE E CONTI CORRENTI</div>
            <button style={s.btnSec} onClick={()=>setShowAddCassa(v=>!v)}><Plus size={13}/> NUOVO</button>
          </div>
          {showAddCassa && (
            <form onSubmit={creaCassa} style={{ background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'10px', padding:'14px', marginBottom:'12px', display:'grid', gridTemplateColumns:'2fr 1fr 2fr 60px 36px', gap:'8px', alignItems:'end' }}>
              <div><label style={s.label}>NOME *</label><input style={s.inp} value={newCassa.nome} onChange={e=>setNewCassa(p=>({...p,nome:e.target.value}))} required placeholder="es. UniCredit"/></div>
              <div><label style={s.label}>TIPO</label><select style={s.inp} value={newCassa.tipo} onChange={e=>setNewCassa(p=>({...p,tipo:e.target.value}))}><option value="BANCA">Banca</option><option value="CONTANTI">Contanti</option><option value="ALTRO">Altro</option></select></div>
              <div><label style={s.label}>IBAN</label><input style={s.inp} value={newCassa.iban} onChange={e=>setNewCassa(p=>({...p,iban:e.target.value}))} placeholder="IT00 X000..."/></div>
              <div><label style={s.label}>COLORE</label><input type="color" style={{ ...s.inp, padding:'3px', height:'36px' }} value={newCassa.colore} onChange={e=>setNewCassa(p=>({...p,colore:e.target.value}))}/></div>
              <button type="submit" style={{ ...s.btnPri, padding:'8px' }}><Check size={13}/></button>
            </form>
          )}
          {casse.map(c=>(
            <div key={c.id} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'14px 18px', marginBottom:'6px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                <div style={{ width:'12px', height:'12px', borderRadius:'50%', background:c.colore, flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:'700', fontSize: 'var(--font-size-sm)' }}>{c.nome}</div>
                  {c.iban && <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', fontFamily:'monospace' }}>{c.iban}</div>}
                  {c.saldo_iniziale != null && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color:'#3B82F6', marginTop:'2px' }}>
                      Saldo iniziale: <strong>{fmt(c.saldo_iniziale)}</strong> al {new Date(c.data_saldo_iniziale).toLocaleDateString('it-IT')}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', padding:'2px 8px', borderRadius:'4px', color: c.tipo==='BANCA'?'#3B82F6':c.tipo==='CONTANTI'?'#F59E0B':'#888', background: c.tipo==='BANCA'?'#3B82F620':c.tipo==='CONTANTI'?'#F59E0B20':'#88888820' }}>{c.tipo}</span>
                {c.tipo === 'BANCA' && (
                  <button style={{ ...s.btnSec, padding:'5px 10px', fontSize: 'var(--font-size-xs)' }}
                    onClick={() => setEditSaldo(p => ({ ...p, [c.id]: { saldo_iniziale: c.saldo_iniziale ?? '', data_saldo_iniziale: c.data_saldo_iniziale ? new Date(c.data_saldo_iniziale).toISOString().slice(0,10) : '' } }))}>
                    SALDO INIZIALE
                  </button>
                )}
              </div>

              {editSaldo[c.id] && (
                <div style={{ marginTop:'12px', paddingTop:'12px', borderTop:'1px solid var(--border,#1E1E1E)', display:'flex', gap:'10px', alignItems:'flex-end', flexWrap:'wrap' }}>
                  <div>
                    <label style={s.label}>DATA SALDO INIZIALE</label>
                    <input type="date" style={{ ...s.inp, width:'160px' }}
                      value={editSaldo[c.id].data_saldo_iniziale}
                      onChange={e => setEditSaldo(p => ({ ...p, [c.id]: { ...p[c.id], data_saldo_iniziale: e.target.value } }))}/>
                  </div>
                  <div>
                    <label style={s.label}>IMPORTO (€)</label>
                    <input type="number" step="0.01" style={{ ...s.inp, width:'140px' }}
                      placeholder="es. 40404.78"
                      value={editSaldo[c.id].saldo_iniziale}
                      onChange={e => setEditSaldo(p => ({ ...p, [c.id]: { ...p[c.id], saldo_iniziale: e.target.value } }))}/>
                  </div>
                  <button style={s.btnPri} onClick={() => salvaSaldoIniziale(c.id)}><Check size={13}/> SALVA</button>
                  <button style={s.btnSec} onClick={() => setEditSaldo(p => { const n = { ...p }; delete n[c.id]; return n; })}><X size={13}/></button>
                  <p style={{ fontSize: 'var(--font-size-xs)', color:'#555', width:'100%', marginTop:'4px', marginBottom:0 }}>
                    Inserisci il saldo del conto in quella data (es. 31/12/2025 → €40.404,78). Il sistema calcolerà: saldo iniziale + tutti i movimenti importati dopo quella data = saldo attuale.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SaldoVerifica({ saldoCalcolato }) {
  const [saldoReale, setSaldoReale] = useState('');
  const sc = saldoCalcolato.saldo_calcolato;
  // Accetta sia "17540.98" sia il formato italiano della banca "17.540,98": con la virgola presente
  // i punti sono separatori delle migliaia (prima "17.540,98" diventava 17.54).
  const parseImporto = (v) => {
    const t = v.trim().replace(/[€\s]/g, '');
    const n = parseFloat(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
    return isNaN(n) ? null : n;
  };
  const reale = saldoReale !== '' ? parseImporto(saldoReale) : null;
  const diff = reale !== null ? sc - reale : null;
  const dataFmt = new Date(saldoCalcolato.data_saldo_iniziale).toLocaleDateString('it-IT');
  const dataUltimo = saldoCalcolato.ultimo_movimento ? new Date(saldoCalcolato.ultimo_movimento).toLocaleDateString('it-IT') : null;

  return (
    <div style={{ background:'var(--bg-card,#141414)', border:'1px solid #3B82F630', borderRadius:'10px', padding:'14px 18px', marginBottom:'14px' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#3B82F6', marginBottom:'10px' }}>
        VERIFICA SALDO — {saldoCalcolato.nome}
      </div>
      <div style={{ display:'flex', gap:'0', alignItems:'stretch', flexWrap:'wrap' }}>

        {/* Formula */}
        <div style={{ display:'flex', alignItems:'center', gap:'10px', flex:1, minWidth:'300px', flexWrap:'wrap' }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800', color: 'var(--text)' }}>
              {fmt(saldoCalcolato.saldo_iniziale)}
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginTop:'2px' }}>saldo al {dataFmt}</div>
          </div>
          <span style={{ color:'#555', fontSize: 'var(--font-size-lg)', fontWeight:'300' }}>+</span>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800', color: saldoCalcolato.tot_movimenti >= 0 ? '#22C55E' : '#EF4444' }}>
              {saldoCalcolato.tot_movimenti >= 0 ? '+' : ''}{fmt(saldoCalcolato.tot_movimenti)}
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginTop:'2px' }}>movimenti importati</div>
          </div>
          <span style={{ color:'#555', fontSize: 'var(--font-size-lg)', fontWeight:'300' }}>=</span>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800', color:'#3B82F6' }}>
              {fmt(sc)}
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color:'#3B82F6', marginTop:'2px', fontWeight:'700' }}>SALDO CALCOLATO{dataUltimo ? ` al ${dataUltimo}` : ''}</div>
          </div>
        </div>

        {/* Verifica manuale */}
        <div style={{ display:'flex', alignItems:'center', gap:'10px', paddingLeft:'20px', borderLeft:'1px solid var(--border,#1E1E1E)', marginLeft:'10px', flexShrink:0 }}>
          <div>
            <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', fontWeight:'700', letterSpacing:'1px', marginBottom:'4px' }}>SALDO REALE SUL CONTO</div>
            <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
              <span style={{ color:'#555', fontSize: 'var(--font-size-sm)' }}>€</span>
              <input
                type="text" placeholder="es. 17.540,98"
                style={{ background:'var(--bg-input,#1A1A1A)', border:`1px solid ${diff === null ? 'var(--border2,#2A2A2A)' : Math.abs(diff) < 0.01 ? '#22C55E' : '#EF4444'}`, borderRadius:'8px', padding:'6px 10px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', fontFamily:"'Barlow Condensed'", fontWeight:'700', outline:'none', width:'130px' }}
                value={saldoReale}
                onChange={e => setSaldoReale(e.target.value)}
              />
            </div>
          </div>
          {diff !== null && (
            <div style={{ textAlign:'center' }}>
              {Math.abs(diff) < 0.01 ? (
                <div style={{ color:'#22C55E', fontWeight:'800', fontSize: 'var(--font-size-sm)' }}>✓ COINCIDE</div>
              ) : (
                <>
                  <div style={{ color:'#EF4444', fontWeight:'800', fontSize: 'var(--font-size-sm)' }}>DIFFERENZA</div>
                  <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#EF4444' }}>
                    {diff > 0 ? '+' : ''}{fmt(diff)}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>{diff > 0 ? 'troppo alto → duplicati?' : 'troppo basso → transazioni mancanti?'}</div>
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

const s = {
  title:     { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:       { color:'var(--text-muted,#888)', fontSize: 'var(--font-size-sm)', marginTop:'4px' },
  btnPri:    { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec:    { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', color:'var(--text,#fff)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnWarn:   { display:'flex', alignItems:'center', gap:'6px', background:'#8B5CF620', color:'#8B5CF6', border:'1px solid #8B5CF640', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  filterSel: { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-xs)', outline:'none' },
  iconBtn:   { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'6px', padding:'6px 8px', color:'var(--text-muted,#888)', cursor:'pointer', display:'inline-flex', alignItems:'center' },
  kpiSmall:  { background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'8px', padding:'8px 14px', display:'flex', alignItems:'center' },
  secTitle:  { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555' },
  label:     { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555', display:'block', marginBottom:'4px' },
  inp:       { background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  th:        { padding:'10px 12px', textAlign:'left', fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#555', borderBottom:'1px solid var(--border,#1E1E1E)' },
  td:        { padding:'9px 12px', fontSize: 'var(--font-size-sm)', verticalAlign:'middle' },
  card:      { background:'var(--bg-card,#141414)', border:'1px solid #F4600C40', borderRadius:'12px', padding:'18px', marginBottom:'16px' },
  inAttesa:  { display:'inline-block', marginRight:'6px', padding:'1px 6px', borderRadius:'4px', border:'1px solid #F59E0B40', background:'#F59E0B15', color:'#D97706', fontSize:'10px', fontWeight:800, letterSpacing:'0.5px', verticalAlign:'middle' },
  cardTitle: { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#F4600C', display:'inline-flex', alignItems:'center' },
};
